import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ResponsiveModal from './ResponsiveModal';
import type { DirectWriteEntry, DirectWriteIgnoredEntry } from '../utils/directWriteTraversal';
import {
  readExifDetails,
  readExifSummary,
  readImageDimensions,
  type DirectWriteExifTags,
  type DirectWriteFileMeta,
} from '../utils/directWriteMetadata';
import {
  getCachedMetadata,
  saveCachedMetadata,
  getBatchCachedMetadata,
} from '../utils/directWriteMetadataStorage';
import { getCachedThumb, saveCachedThumb, clearThumbCache } from '../utils/directWriteThumbStorage';
import { canUseOpfsThumbs, clearOpfsThumbs, getOpfsThumb, saveOpfsThumb } from '../utils/directWriteThumbOpfs';
import '../App.css';

const LONG_PRESS_MS = 320;
const COLUMN_MIN = 120;
const ROW_HEIGHT = 190;
const GRID_GAP = 12;
const OVERSCAN = 3;
const THUMB_FORMAT = 'image/jpeg';
const EXIF_THUMB_SCAN_BYTES = 128 * 1024;
const EXIF_THUMB_SCAN_BYTES_ANDROID = 64 * 1024;
const EXIF_HEADER = 'Exif\u0000\u0000';
const EXIF_ADAPTIVE_SLOW_MS = 1500;
const EXIF_ADAPTIVE_SLOW_LIMIT = 1;
const THUMB_PRIORITY_GRACE_MS = 200;
const PREFER_FULL_THUMBS = true;
const THUMB_CACHE_VERSION = 'full-v1';
const TINY_FILE_BYTES = 5 * 1024;
const META_BATCH_SIZE = 24;
const META_CONCURRENCY = 6;
const SYSTEM_NAMES = new Set(['thumbs.db', 'desktop.ini', '.ds_store']);
const LARGE_FOLDER_THRESHOLD = 200;
const SORT_OPTIONS = [
  { value: 'default', label: 'Default (scan order)' },
  { value: 'name', label: 'Name' },
  { value: 'path', label: 'Path' },
  { value: 'lastModified', label: 'Last modified' },
  { value: 'size', label: 'File size' },
] as const;

type ThumbValue = string | ImageBitmap;
type ThumbBitmapResult = {
  bitmap: ImageBitmap | null;
  decoder: 'imageDecoder' | 'createImageBitmap' | 'worker' | null;
};

const ThumbCanvas: React.FC<{
  bitmap: ImageBitmap;
  size: number;
  alt: string;
  highQuality: boolean;
}> = ({ bitmap, size, alt, highQuality }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const displayWidth = Math.max(1, Math.round(rect.width || size));
    const displayHeight = Math.max(1, Math.round(rect.height || size));
    const dpr = typeof window !== 'undefined' ? Math.max(1, window.devicePixelRatio || 1) : 1;
    canvas.width = Math.round(displayWidth * dpr);
    canvas.height = Math.round(displayHeight * dpr);
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if ('imageSmoothingQuality' in ctx) {
      ctx.imageSmoothingQuality = highQuality ? 'high' : 'low';
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, displayWidth, displayHeight);
    const scale = Math.min(displayWidth / bitmap.width, displayHeight / bitmap.height, 1);
    const targetWidth = Math.max(1, Math.round(bitmap.width * scale));
    const targetHeight = Math.max(1, Math.round(bitmap.height * scale));
    const offsetX = Math.floor((displayWidth - targetWidth) / 2);
    const offsetY = Math.floor((displayHeight - targetHeight) / 2);
    ctx.drawImage(bitmap, offsetX, offsetY, targetWidth, targetHeight);
  }, [bitmap, highQuality, size]);

  return (
    <canvas
      ref={canvasRef}
      aria-label={alt}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
};

const DATE_METADATA_FIELDS = new Set<keyof DirectWriteExifTags>([
  'dateTimeOriginal',
  'dateTimeDigitized',
  'gpsDateStamp',
]);
const TIME_METADATA_FIELDS = new Set<keyof DirectWriteExifTags>(['gpsTimeStamp']);
const NUMERIC_METADATA_FIELDS = new Set<keyof DirectWriteExifTags>([
  'iso',
  'fNumber',
  'focalLength',
  'gpsAltitude',
  'gpsLatitude',
  'gpsLongitude',
  'gpsImgDirection',
  'exposureTime',
  'exposureBiasValue',
]);

const METADATA_FIELDS: Array<{ value: keyof DirectWriteExifTags; label: string }> = [
  { value: 'make', label: 'Make' },
  { value: 'model', label: 'Model' },
  { value: 'lensModel', label: 'Lens model' },
  { value: 'orientation', label: 'Orientation' },
  { value: 'whiteBalance', label: 'White balance' },
  { value: 'flash', label: 'Flash' },
  { value: 'sceneCaptureType', label: 'Scene capture type' },
  { value: 'exposureProgram', label: 'Exposure program' },
  { value: 'iso', label: 'ISO' },
  { value: 'exposureTime', label: 'Exposure time' },
  { value: 'exposureBiasValue', label: 'Exposure bias value' },
  { value: 'fNumber', label: 'F-number' },
  { value: 'focalLength', label: 'Focal length' },
  { value: 'gpsAltitude', label: 'GPS altitude' },
  { value: 'gpsAltitudeRef', label: 'GPS altitude ref' },
  { value: 'gpsLatitude', label: 'GPS latitude' },
  { value: 'gpsLatitudeRef', label: 'GPS latitude ref' },
  { value: 'gpsLongitude', label: 'GPS longitude' },
  { value: 'gpsLongitudeRef', label: 'GPS longitude ref' },
  { value: 'gpsDateStamp', label: 'GPS date stamp (UTC)' },
  { value: 'gpsTimeStamp', label: 'GPS time stamp (UTC)' },
  { value: 'gpsImgDirection', label: 'GPS image direction' },
  { value: 'gpsImgDirectionRef', label: 'GPS image direction ref' },
  { value: 'dateTimeOriginal', label: 'Date/time original' },
  { value: 'dateTimeDigitized', label: 'Date/time digitized' },
  { value: 'exposureMode', label: 'Exposure mode' },
  { value: 'meteringMode', label: 'Metering mode' },
];

type ThumbProfile = {
  maxThumbs: number;
  thumbSize: number;
  decodeConcurrency: number;
  prefetchRows: number;
  quality: number;
};

const DESKTOP_PROFILE: ThumbProfile = {
  maxThumbs: 220,
  thumbSize: 210,
  decodeConcurrency: 5,
  prefetchRows: 5,
  quality: 0.74,
};

const DESKTOP_PROFILE_HQ: ThumbProfile = {
  maxThumbs: 180,
  thumbSize: 260,
  decodeConcurrency: 4,
  prefetchRows: 4,
  quality: 0.86,
};

const ANDROID_PROFILE: ThumbProfile = {
  maxThumbs: 120,
  thumbSize: 150,
  decodeConcurrency: 1,
  prefetchRows: 1,
  quality: 0.72,
};

const ANDROID_PROFILE_HQ: ThumbProfile = {
  maxThumbs: 100,
  thumbSize: 180,
  decodeConcurrency: 1,
  prefetchRows: 1,
  quality: 0.82,
};

const getThumbProfile = (mode: 'desktop' | 'android', highQuality: boolean) => {
  const base =
    mode === 'android'
      ? highQuality
        ? ANDROID_PROFILE_HQ
        : ANDROID_PROFILE
      : highQuality
        ? DESKTOP_PROFILE_HQ
        : DESKTOP_PROFILE;
  if (typeof navigator === 'undefined') return base;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (!memory) return base;

  if (memory <= 2) {
    return {
      ...base,
      maxThumbs: Math.max(80, Math.round(base.maxThumbs * 0.55)),
      thumbSize: Math.max(140, Math.round(base.thumbSize * 0.8)),
      decodeConcurrency: Math.max(1, base.decodeConcurrency - 2),
      prefetchRows: Math.max(1, base.prefetchRows - 2),
    };
  }

  if (memory <= 4) {
    return {
      ...base,
      maxThumbs: Math.max(120, Math.round(base.maxThumbs * 0.75)),
      thumbSize: Math.max(160, Math.round(base.thumbSize * 0.9)),
      decodeConcurrency: Math.max(1, base.decodeConcurrency - 1),
      prefetchRows: Math.max(1, base.prefetchRows - 1),
    };
  }

  return base;
};

const readAscii = (view: DataView, start: number, length: number) => {
  let out = '';
  const end = Math.min(view.byteLength, start + length);
  for (let i = start; i < end; i += 1) {
    out += String.fromCharCode(view.getUint8(i));
  }
  return out;
};

const findExifThumb = (view: DataView, tiffStart: number) => {
  if (tiffStart + 8 > view.byteLength) return null;
  const endian = readAscii(view, tiffStart, 2);
  const littleEndian = endian === 'II';
  if (!littleEndian && endian !== 'MM') return null;
  const magic = view.getUint16(tiffStart + 2, littleEndian);
  if (magic !== 0x002a) return null;
  const ifd0Offset = view.getUint32(tiffStart + 4, littleEndian);
  const ifd0Pos = tiffStart + ifd0Offset;
  if (ifd0Pos + 2 > view.byteLength) return null;
  const ifd0Entries = view.getUint16(ifd0Pos, littleEndian);
  const ifd0End = ifd0Pos + 2 + ifd0Entries * 12;
  if (ifd0End + 4 > view.byteLength) return null;
  const ifd1Offset = view.getUint32(ifd0End, littleEndian);
  if (!ifd1Offset) return null;
  const ifd1Pos = tiffStart + ifd1Offset;
  if (ifd1Pos + 2 > view.byteLength) return null;
  const ifd1Entries = view.getUint16(ifd1Pos, littleEndian);
  let thumbOffset: number | null = null;
  let thumbLength: number | null = null;
  for (let i = 0; i < ifd1Entries; i += 1) {
    const entryOffset = ifd1Pos + 2 + i * 12;
    if (entryOffset + 12 > view.byteLength) break;
    const tag = view.getUint16(entryOffset, littleEndian);
    const valueOffset = view.getUint32(entryOffset + 8, littleEndian);
    if (tag === 0x0201) {
      thumbOffset = valueOffset;
    } else if (tag === 0x0202) {
      thumbLength = valueOffset;
    }
  }
  if (!thumbOffset || !thumbLength) return null;
  return { offset: tiffStart + thumbOffset, length: thumbLength };
};

const readEmbeddedJpegThumb = async (file: File, maxBytes = EXIF_THUMB_SCAN_BYTES): Promise<Blob | null> => {
  if (file.size < 4) return null;
  const headerSize = Math.min(file.size, maxBytes);
  const buffer = await file.slice(0, headerSize).arrayBuffer();
  const view = new DataView(buffer);
  if (view.getUint16(0, false) !== 0xffd8) return null;
  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) break;
    const marker = view.getUint8(offset + 1);
    if (marker === 0xda || marker === 0xd9) {
      break;
    }
    const size = view.getUint16(offset + 2, false);
    if (size < 2) break;
    if (marker === 0xe1) {
      const payloadStart = offset + 4;
      if (payloadStart + 6 <= view.byteLength) {
        const header = readAscii(view, payloadStart, 6);
        if (header === EXIF_HEADER) {
          const tiffStart = payloadStart + 6;
          const thumbInfo = findExifThumb(view, tiffStart);
          if (thumbInfo && thumbInfo.length > 0) {
            const end = thumbInfo.offset + thumbInfo.length;
            if (end <= file.size) {
              return file.slice(thumbInfo.offset, end, THUMB_FORMAT);
            }
          }
        }
      }
    }
    offset += 2 + size;
  }
  return null;
};

export type DirectWriteSelection = {
  allSelected: boolean;
  selectedIds: string[];
  excludedIds: string[];
};

interface DirectWritePickerModalProps {
  isOpen: boolean;
  entries: DirectWriteEntry[];
  scanning: boolean;
  warnings: string[];
  ignoredFiles: DirectWriteIgnoredEntry[];
  ignoredCount: number;
  rootName?: string | null;
  rootHandle?: FileSystemDirectoryHandle | null;
  platform?: 'desktop' | 'android';
  onClose: () => void;
  onConfirm: (selection: DirectWriteSelection) => void;
}

const DirectWritePickerModal: React.FC<DirectWritePickerModalProps> = ({
  isOpen,
  entries,
  scanning,
  warnings,
  ignoredFiles,
  ignoredCount,
  rootName,
  platform, // Default removed to allow undefined check
  onClose,
  onConfirm,
}) => {
  const [query, setQuery] = useState('');
  const [allSelected, setAllSelected] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deselectedIds, setDeselectedIds] = useState<Set<string>>(new Set());
  const [excludeHidden, setExcludeHidden] = useState(true);
  const [excludeTiny, setExcludeTiny] = useState(true);
  const [highQualityThumbs, setHighQualityThumbs] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [timeStart, setTimeStart] = useState('08:00');
  const [timeEnd, setTimeEnd] = useState('18:00');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [nameInclude, setNameInclude] = useState('');
  const [nameExclude, setNameExclude] = useState('');
  const [regexFilter, setRegexFilter] = useState('');
  const [rangePrefix, setRangePrefix] = useState('IMG_');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [sortBy, setSortBy] = useState<(typeof SORT_OPTIONS)[number]['value']>('default');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [metadataField, setMetadataField] = useState<keyof DirectWriteExifTags>('model');
  const [metadataValue, setMetadataValue] = useState('');
  const [metadataDateStart, setMetadataDateStart] = useState('');
  const [metadataDateEnd, setMetadataDateEnd] = useState('');
  const [metadataRangeMin, setMetadataRangeMin] = useState('');
  const [metadataRangeMax, setMetadataRangeMax] = useState('');
  const [metadataTimeStart, setMetadataTimeStart] = useState('');
  const [metadataTimeEnd, setMetadataTimeEnd] = useState('');
  const [metadataMenuOpen, setMetadataMenuOpen] = useState(false);
  const [preScanEnabled, setPreScanEnabled] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('directWrite_preScan');
      if (saved !== null) return saved === 'true';
    }
    // Safe default: only enable if explicitly desktop. 
    // If platform is undefined (detecting) or android, start false.
    return platform === 'desktop';
  });
  const [preScanStatus, setPreScanStatus] = useState<string | null>(null);
  const [preScanProgress, setPreScanProgress] = useState<number | null>(null);
  const [metadataReady, setMetadataReady] = useState(false);
  const [ruleStatus, setRuleStatus] = useState<string | null>(null);
  const [gridSize, setGridSize] = useState({ width: 0, height: 0 });
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbTick, forceThumbTick] = useState(0);
  const [metaTick, forceMetaTick] = useState(0);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [rangeAnchorId, setRangeAnchorId] = useState<string | null>(null);
  const [metaFilterActivated, setMetaFilterActivated] = useState(false);
  const [useSidePanel, setUseSidePanel] = useState(false);
  const [clearingThumbs, setClearingThumbs] = useState(false);
  const [fastPreviewEnabled, setFastPreviewEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('directWrite_fastPreview');
    if (saved !== null) return saved === 'true';
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    return /Android/i.test(ua);
  });
  const [opfsThumbsEnabled, setOpfsThumbsEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = localStorage.getItem('directWrite_opfsThumbs');
    if (saved !== null) return saved === 'true';
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const isAndroid = /Android/i.test(ua);
    return isAndroid && canUseOpfsThumbs();
  });
  const isLargeFolder = entries.length >= LARGE_FOLDER_THRESHOLD;
  const thumbProfile = useMemo(() => {
    const base = getThumbProfile(platform || 'android', highQualityThumbs);
    if (platform === 'android') {
      const adjusted = {
        ...base,
        prefetchRows: 0,
      };
      if (isLargeFolder) {
        const trimmed = {
          ...adjusted,
          maxThumbs: Math.min(base.maxThumbs, 80),
        };
        const memory =
          typeof navigator !== 'undefined'
            ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 0
            : 0;
        const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 0 : 0;
        if (memory >= 8 && cores >= 8) {
          return { ...trimmed, decodeConcurrency: Math.max(trimmed.decodeConcurrency, 2) };
        }
        return trimmed;
      }
      const memory =
        typeof navigator !== 'undefined'
          ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 0
          : 0;
      const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 0 : 0;
      if (memory >= 8 && cores >= 8) {
        return { ...adjusted, decodeConcurrency: Math.max(adjusted.decodeConcurrency, 2) };
      }
      return adjusted;
    }
    return base;
  }, [highQualityThumbs, isLargeFolder, platform]);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const longPressTimerRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef(false);
  const loadingThumbsRef = useRef<Set<string>>(new Set());
  const thumbMapRef = useRef<Map<string, ThumbValue>>(new Map());
  const thumbSizeRef = useRef<Map<string, number>>(new Map());
  const thumbOrderRef = useRef<string[]>([]);
  const thumbQueueRef = useRef<DirectWriteEntry[]>([]);
  const thumbQueuedRef = useRef<Set<string>>(new Set());
  const thumbCacheWritingRef = useRef<Set<string>>(new Set());
  const thumbWorkerRef = useRef<Worker | null>(null);
  const thumbWorkerRequestRef = useRef(0);
  const thumbWorkerPendingRef = useRef(
    new Map<
      number,
      { resolve: (value: ImageBitmap | null) => void; reject: (error: Error) => void; timeout?: number }
    >()
  );
  const exifAdaptiveRef = useRef({
    disabled: false,
    attempts: 0,
    slowCount: 0,
  });
  const thumbTimingRef = useRef({
    loadingTotalMs: 0,
    loadingCount: 0,
    loadingLastMs: 0,
    getFileTotalMs: 0,
    getFileCount: 0,
    getFileLastMs: 0,
    decodeTotalMs: 0,
    decodeCount: 0,
    decodeLastMs: 0,
    decodeOnlyTotalMs: 0,
    decodeOnlyCount: 0,
    decodeOnlyLastMs: 0,
    cacheHit: 0,
    cacheMiss: 0,
    opfsHit: 0,
    opfsMiss: 0,
    exifHit: 0,
    exifMiss: 0,
    exifSkip: 0,
    exifTotalMs: 0,
    exifCount: 0,
    exifLastMs: 0,
    workerCount: 0,
    workerFail: 0,
    workerLastError: '',
    imageDecoderCount: 0,
    createImageBitmapCount: 0,
    imageDecoderAttempt: 0,
    imageDecoderFail: 0,
    imageDecoderLastError: '',
  });
  const thumbGenerationRef = useRef(0);
  const thumbActiveRef = useRef(0);
  const visibleIdsRef = useRef<Set<string>>(new Set());
  const metaCacheRef = useRef<Map<string, DirectWriteFileMeta>>(new Map());
  const metaLoadingRef = useRef<Set<string>>(new Set());
  const preScanTokenRef = useRef(0);
  const preScanProgressRef = useRef({ total: 0, completed: 0, lastPercent: -1 });
  const metadataReadyRef = useRef(false);
  const isUserScrollingRef = useRef(false);
  const scrollIdleRef = useRef<number | null>(null);
  const [debugMode, setDebugMode] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search.includes('debug=1')) {
      setDebugMode(true);
    }
  }, []);

  const waitForThumbnailIdle = useCallback(async (maxWaitMs = 600) => {
    if (typeof window === 'undefined') return;
    const start = Date.now();
    while (
      (thumbActiveRef.current > 0 || thumbQueueRef.current.length > 0) &&
      Date.now() - start < maxWaitMs
    ) {
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
  }, []);

  useEffect(() => {
    metadataReadyRef.current = metadataReady;
  }, [metadataReady]);

  useEffect(() => {
    // If user hasn't set a preference (localStorage is null), adapt to the detected platform.
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem('directWrite_preScan');
    if (saved !== null) return;
    if (platform === 'desktop') {
      setPreScanEnabled(true);
    } else if (platform === 'android') {
      setPreScanEnabled(false);
    }
  }, [platform]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('directWrite_preScan', String(preScanEnabled));
    } catch {
      // ignore storage errors
    }
  }, [preScanEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('directWrite_opfsThumbs', String(opfsThumbsEnabled));
    } catch {
      // ignore storage errors
    }
  }, [opfsThumbsEnabled]);

  useEffect(() => {
    if (platform && platform !== 'android' && opfsThumbsEnabled) {
      setOpfsThumbsEnabled(false);
    }
  }, [opfsThumbsEnabled, platform]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('directWrite_fastPreview', String(fastPreviewEnabled));
    } catch {
      // ignore storage errors
    }
  }, [fastPreviewEnabled]);

  const resetSelection = () => {
    setAllSelected(false);
    setSelectedIds(new Set());
    setDeselectedIds(new Set());
    setRangeAnchorId(null);
    setMultiSelectMode(false);
    setMetaFilterActivated(false);
    setShowSelectedOnly(false);
  };

  useEffect(() => {
    if (!isOpen) {
      preScanTokenRef.current += 1;
      thumbGenerationRef.current += 1;
      thumbQueueRef.current = [];
      thumbQueuedRef.current.clear();
      thumbActiveRef.current = 0;
      visibleIdsRef.current = new Set();
      setQuery('');
      setExcludeHidden(true);
      setExcludeTiny(true);
      setHighQualityThumbs(false);
      setShowAdvanced(false);
      setNameInclude('');
      setNameExclude('');
      setRegexFilter('');
      setRangePrefix('IMG_');
      setRangeStart('');
      setRangeEnd('');
      setDateStart('');
      setDateEnd('');
      setShowSelectedOnly(false);
      setSortBy('default');
      setSortDirection('asc');
      setMetadataField('model');
      setMetadataValue('');
      setMetadataDateStart('');
      setMetadataDateEnd('');
      setMetadataRangeMin('');
      setMetadataRangeMax('');
      setMetadataTimeStart('');
      setMetadataTimeEnd('');
      setMetadataMenuOpen(false);
      setPreScanStatus(null);
      setPreScanProgress(null);
      setMetadataReady(false);
      setRuleStatus(null);
      resetSelection();
      const thumbs = thumbMapRef.current;
      thumbs.forEach((value) => {
        if (typeof value === 'string') {
          if (!value.startsWith('STATUS:') && !value.startsWith('ERROR:')) {
            URL.revokeObjectURL(value);
          }
          return;
        }
        value.close();
      });
      thumbs.clear();
      thumbSizeRef.current.clear();
      thumbOrderRef.current = [];
      loadingThumbsRef.current.clear();
      thumbCacheWritingRef.current.clear();
      if (thumbWorkerRef.current) {
        thumbWorkerRef.current.terminate();
        thumbWorkerRef.current = null;
      }
      thumbWorkerPendingRef.current.forEach(({ resolve, timeout }) => {
        if (typeof window !== 'undefined' && typeof timeout === 'number') {
          window.clearTimeout(timeout);
        }
        resolve(null);
      });
      thumbWorkerPendingRef.current.clear();
      exifAdaptiveRef.current = {
        disabled: false,
        attempts: 0,
        slowCount: 0,
      };
      thumbTimingRef.current = {
        loadingTotalMs: 0,
        loadingCount: 0,
        loadingLastMs: 0,
        getFileTotalMs: 0,
        getFileCount: 0,
        getFileLastMs: 0,
        decodeTotalMs: 0,
        decodeCount: 0,
        decodeLastMs: 0,
        decodeOnlyTotalMs: 0,
        decodeOnlyCount: 0,
        decodeOnlyLastMs: 0,
        cacheHit: 0,
        cacheMiss: 0,
        opfsHit: 0,
        opfsMiss: 0,
        exifHit: 0,
        exifMiss: 0,
        exifSkip: 0,
        exifTotalMs: 0,
        exifCount: 0,
        exifLastMs: 0,
        workerCount: 0,
        workerFail: 0,
        workerLastError: '',
        imageDecoderCount: 0,
        createImageBitmapCount: 0,
        imageDecoderAttempt: 0,
        imageDecoderFail: 0,
        imageDecoderLastError: '',
      };
      forceThumbTick((prev) => prev + 1);
      metaCacheRef.current.clear();
      metaLoadingRef.current.clear();
      forceMetaTick((prev) => prev + 1);
      return;
    }
    resetSelection();
  }, [isOpen]);


  useEffect(() => {
    if (!isOpen) return;
    const node = gridRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entriesList) => {
      const entry = entriesList[0];
      if (!entry) return;
      setGridSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setMetadataReady(false);
  }, [entries.length, excludeHidden, excludeTiny, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    thumbGenerationRef.current += 1;
    thumbQueueRef.current = [];
    thumbQueuedRef.current.clear();
    thumbActiveRef.current = 0;
    visibleIdsRef.current = new Set();
    const thumbs = thumbMapRef.current;
    thumbs.forEach((value) => {
      if (typeof value === 'string') {
        if (!value.startsWith('STATUS:') && !value.startsWith('ERROR:')) {
          URL.revokeObjectURL(value);
        }
        return;
      }
      value.close();
    });
    thumbs.clear();
    thumbSizeRef.current.clear();
    thumbOrderRef.current = [];
    loadingThumbsRef.current.clear();
    thumbCacheWritingRef.current.clear();
    exifAdaptiveRef.current = {
      disabled: false,
      attempts: 0,
      slowCount: 0,
    };
    thumbTimingRef.current = {
      loadingTotalMs: 0,
      loadingCount: 0,
      loadingLastMs: 0,
      getFileTotalMs: 0,
      getFileCount: 0,
      getFileLastMs: 0,
      decodeTotalMs: 0,
      decodeCount: 0,
      decodeLastMs: 0,
      decodeOnlyTotalMs: 0,
      decodeOnlyCount: 0,
      decodeOnlyLastMs: 0,
      cacheHit: 0,
      cacheMiss: 0,
      opfsHit: 0,
      opfsMiss: 0,
      exifHit: 0,
      exifMiss: 0,
      exifSkip: 0,
      exifTotalMs: 0,
      exifCount: 0,
      exifLastMs: 0,
      workerCount: 0,
      workerFail: 0,
      workerLastError: '',
      imageDecoderCount: 0,
      createImageBitmapCount: 0,
      imageDecoderAttempt: 0,
      imageDecoderFail: 0,
      imageDecoderLastError: '',
    };
    forceThumbTick((prev) => prev + 1);
  }, [isOpen, thumbProfile]);

  const updateMeta = useCallback((id: string, meta: DirectWriteFileMeta) => {
    metaCacheRef.current.set(id, meta);
    forceMetaTick((prev) => prev + 1);
  }, []);

  const handleClearThumbCache = useCallback(async () => {
    if (clearingThumbs) return;
    setClearingThumbs(true);
    try {
      thumbGenerationRef.current += 1;
      thumbQueueRef.current = [];
      thumbQueuedRef.current.clear();
      thumbActiveRef.current = 0;
      visibleIdsRef.current = new Set();
      const thumbs = thumbMapRef.current;
      thumbs.forEach((value) => {
        if (typeof value === 'string') {
          if (!value.startsWith('STATUS:') && !value.startsWith('ERROR:')) {
            URL.revokeObjectURL(value);
          }
          return;
        }
        value.close();
      });
      thumbs.clear();
      thumbSizeRef.current.clear();
      thumbOrderRef.current = [];
      loadingThumbsRef.current.clear();
      thumbCacheWritingRef.current.clear();
      forceThumbTick((prev) => prev + 1);
      await clearThumbCache();
      if (canUseOpfsThumbs()) {
        await clearOpfsThumbs();
      }
    } finally {
      setClearingThumbs(false);
    }
  }, [clearingThumbs]);

  const handleGridScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
    if (typeof window === 'undefined') return;
    isUserScrollingRef.current = true;
    if (scrollIdleRef.current) {
      window.clearTimeout(scrollIdleRef.current);
    }
    scrollIdleRef.current = window.setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 160);
  }, []);

  const ensureMeta = useCallback(
    async (
      entry: DirectWriteEntry,
      fields: Array<'size' | 'lastModified' | 'exif' | 'exifTags' | 'dimensions'>
    ) => {
      const cached = metaCacheRef.current.get(entry.id);
      const needsSize = fields.includes('size') && typeof cached?.size !== 'number';
      const needsLastModified = fields.includes('lastModified') && typeof cached?.lastModified !== 'number';
      const needsExif = fields.includes('exif') && typeof cached?.hasExif !== 'boolean';
      const needsGps = fields.includes('exif') && typeof cached?.hasGps !== 'boolean';
      const needsExifTags = fields.includes('exifTags') && cached?.exifTags === undefined;
      const needsDimensions =
        fields.includes('dimensions') &&
        (typeof cached?.width !== 'number' || typeof cached?.height !== 'number');

      if (!needsSize && !needsLastModified && !needsExif && !needsGps && !needsExifTags && !needsDimensions) {
        return cached as DirectWriteFileMeta;
      }

      if (metaLoadingRef.current.has(entry.id)) {
        return cached;
      }

      metaLoadingRef.current.add(entry.id);
      try {
        if (platform !== 'desktop') {
          // Bypass file reads on non-desktop (or unknown) to prevent lock contention.
          // Return cached or basic mock.
          metaLoadingRef.current.delete(entry.id);
          return cached || { size: 0, lastModified: 0 } as any;
        }

        let file: File | undefined;
        let directError: any = null;
        const useParentFirst = !!entry.parentHandle;

        if (useParentFirst) {
          try {
            const refreshed = await entry.parentHandle!.getFileHandle(entry.name);
            file = await refreshed.getFile();
          } catch (err) {
            directError = err;
          }
        }

        if (!file) {
          try {
            file = await entry.handle.getFile();
          } catch (err) {
            if (!directError) {
              directError = err;
            }
          }
        }

        if (!file) {
          throw directError;
        }

        // Check IndexedDB cache first
        const dbCache = await getCachedMetadata(entry.id);
        if (
          dbCache &&
          dbCache.lastModified === file.lastModified &&
          dbCache.size === file.size
        ) {
          const cachedMeta = dbCache.meta;

          // Check if cache satisfies all needs
          const cacheSatisfies =
            (!needsSize || typeof cachedMeta.size === 'number') &&
            (!needsLastModified || typeof cachedMeta.lastModified === 'number') &&
            (!needsExif || typeof cachedMeta.hasExif === 'boolean') &&
            (!needsGps || typeof cachedMeta.hasGps === 'boolean') &&
            (!needsExifTags || cachedMeta.exifTags !== undefined) &&
            (!needsDimensions || (typeof cachedMeta.width === 'number' && typeof cachedMeta.height === 'number'));

          if (cacheSatisfies) {
            updateMeta(entry.id, cachedMeta);
            return cachedMeta;
          }
          // If cache exists but doesn't have enough fields, we proceed to read, 
          // but we can seed our current meta with cache to avoid re-reading what we have.
          // However, simple merge is safer.
        }

        const meta: DirectWriteFileMeta = {
          size: cached?.size ?? file.size,
          lastModified: cached?.lastModified ?? file.lastModified,
          type: cached?.type ?? file.type,
          hasExif: cached?.hasExif,
          hasGps: cached?.hasGps,
          width: cached?.width,
          height: cached?.height,
          // Merge from DB cache if available to keeping existing knowns
          ...(dbCache?.meta || {}),
        };

        // If we have DB cache, ensure we overwrite with fresh file info for base props
        meta.size = file.size;
        meta.lastModified = file.lastModified;

        if (needsExifTags && !meta.exifTags) {
          const exif = await readExifDetails(file);
          meta.hasExif = exif.hasExif;
          meta.hasGps = exif.hasGps;
          meta.exifTags = exif.tags ?? {};
        } else if ((needsExif || needsGps) && (meta.hasExif === undefined || meta.hasGps === undefined)) {
          const exif = await readExifSummary(file);
          meta.hasExif = exif.hasExif;
          meta.hasGps = exif.hasGps;
        }

        if (needsDimensions && (meta.width === undefined || meta.height === undefined)) {
          const dims = await readImageDimensions(file);
          if (dims) {
            meta.width = dims.width;
            meta.height = dims.height;
          }
        }

        updateMeta(entry.id, meta);
        // Persist to DB
        void saveCachedMetadata(entry.id, file.size, file.lastModified, meta);

        return meta;
      } catch {
        return cached;
      } finally {
        metaLoadingRef.current.delete(entry.id);
      }
    },
    [platform, updateMeta]
  );

  const ensureMetaForEntries = useCallback(
    async (
      list: DirectWriteEntry[],
      fields: Array<'size' | 'lastModified' | 'exif' | 'exifTags' | 'dimensions'>,
      label: string
    ) => {
      if (!list.length) return;
      if (thumbActiveRef.current === 0 && thumbQueueRef.current.length === 0) {
        await new Promise((resolve) => setTimeout(resolve, THUMB_PRIORITY_GRACE_MS));
      }
      const trackExifTagsProgress = fields.length === 1 && fields[0] === 'exifTags';
      if (trackExifTagsProgress && metadataReadyRef.current) {
        return;
      }
      let completed = 0;
      let total = list.length;
      let lastPercent = -1;
      const reportProgress = () => {
        const percent = Math.min(100, Math.round((completed / total) * 100));
        if (percent !== lastPercent) {
          lastPercent = percent;
          setRuleStatus(`${label} ${percent}%`);
        }
      };

      if (trackExifTagsProgress) {
        completed = list.reduce((count, entry) => {
          return metaCacheRef.current.get(entry.id)?.exifTags !== undefined ? count + 1 : count;
        }, 0);
        if (completed >= total) {
          setMetadataReady(true);
          setRuleStatus(null);
          return;
        }
        reportProgress();
      } else {
        setRuleStatus(label);
      }
      for (let i = 0; i < list.length; i += META_BATCH_SIZE) {
        await waitForThumbnailIdle();
        const busy = thumbActiveRef.current > 0 || thumbQueueRef.current.length > 0;
        const concurrency = busy ? 1 : META_CONCURRENCY;
        const slice = list.slice(i, i + META_BATCH_SIZE);
        const queue = [...slice];
        const workers = Array.from({ length: concurrency }, async () => {
          while (queue.length) {
            const item = queue.shift();
            if (!item) return;
            const hadExifTags =
              trackExifTagsProgress && metaCacheRef.current.get(item.id)?.exifTags !== undefined;
            await ensureMeta(item, fields);
            if (
              trackExifTagsProgress &&
              !hadExifTags &&
              metaCacheRef.current.get(item.id)?.exifTags !== undefined
            ) {
              completed += 1;
            }
          }
        });
        await Promise.all(workers);
        if (trackExifTagsProgress) {
          reportProgress();
        } else {
          setRuleStatus(`${label} ${Math.min(100, Math.round(((i + slice.length) / list.length) * 100))}%`);
        }
      }
      if (trackExifTagsProgress) {
        const ready = list.every(
          (entry) => metaCacheRef.current.get(entry.id)?.exifTags !== undefined
        );
        setMetadataReady(ready);
      }
      setRuleStatus(null);
    },
    [ensureMeta, waitForThumbnailIdle]
  );

  const ensureMetaForEntriesSilent = useCallback(
    async (
      list: DirectWriteEntry[],
      fields: Array<'size' | 'lastModified' | 'exif' | 'exifTags' | 'dimensions'>
    ) => {
      if (!list.length) return;
      if (thumbActiveRef.current === 0 && thumbQueueRef.current.length === 0) {
        await new Promise((resolve) => setTimeout(resolve, THUMB_PRIORITY_GRACE_MS));
      }
      for (let i = 0; i < list.length; i += META_BATCH_SIZE) {
        await waitForThumbnailIdle();
        const busy = thumbActiveRef.current > 0 || thumbQueueRef.current.length > 0;
        const concurrency = busy ? 1 : META_CONCURRENCY;
        const slice = list.slice(i, i + META_BATCH_SIZE);
        const queue = [...slice];
        const workers = Array.from({ length: concurrency }, async () => {
          while (queue.length) {
            const item = queue.shift();
            if (!item) return;
            await ensureMeta(item, fields);
          }
        });
        await Promise.all(workers);
      }
    },
    [ensureMeta, waitForThumbnailIdle]
  );

  const getPendingEntries = useCallback(
    (
      list: DirectWriteEntry[],
      fields: Array<'size' | 'lastModified' | 'exif' | 'exifTags' | 'dimensions'>
    ) => {
      return list.filter((entry) => {
        const cached = metaCacheRef.current.get(entry.id);
        return fields.some((field) => {
          if (field === 'size') return typeof cached?.size !== 'number';
          if (field === 'lastModified') return typeof cached?.lastModified !== 'number';
          if (field === 'exif') return typeof cached?.hasExif !== 'boolean' || typeof cached?.hasGps !== 'boolean';
          if (field === 'exifTags') return cached?.exifTags === undefined;
          if (field === 'dimensions') {
            return typeof cached?.width !== 'number' || typeof cached?.height !== 'number';
          }
          return false;
        });
      });
    },
    []
  );

  const runPreScan = useCallback(
    async (
      list: DirectWriteEntry[],
      fields: Array<'size' | 'lastModified' | 'exif' | 'exifTags' | 'dimensions'>,
      token: number,
      onProgress?: () => void
    ) => {
      const pending = getPendingEntries(list, fields);
      if (!pending.length) return;
      if (thumbActiveRef.current === 0 && thumbQueueRef.current.length === 0) {
        await new Promise((resolve) => setTimeout(resolve, THUMB_PRIORITY_GRACE_MS));
      }
      for (let i = 0; i < pending.length; i += META_BATCH_SIZE) {
        // Yield to main thread to ensure UI responsiveness (especially for scrolling/thumbnails on mobile)
        await new Promise((resolve) => setTimeout(resolve, 0));

        if (preScanTokenRef.current !== token) return;
        await waitForThumbnailIdle();
        const busy = thumbActiveRef.current > 0 || thumbQueueRef.current.length > 0;
        const concurrency = busy ? 1 : META_CONCURRENCY;
        const slice = pending.slice(i, i + META_BATCH_SIZE);
        const queue = [...slice];
        const workers = Array.from({ length: concurrency }, async () => {
          while (queue.length) {
            if (preScanTokenRef.current !== token) return;
            const item = queue.shift();
            if (!item) return;
            await ensureMeta(item, fields);
            onProgress?.();
          }
        });
        await Promise.all(workers);
      }
    },
    [ensureMeta, getPendingEntries, waitForThumbnailIdle]
  );

  useEffect(() => {
    // Only run expensive size check if explicitly desktop.
    // If platform is undefined or android, skip automatic size scan to prevent IO freeze.
    if (platform !== 'desktop') return;
    void ensureMetaForEntries(entries, ['size'], 'Preparing safety filters...');
  }, [entries, excludeTiny, ensureMetaForEntries, isOpen, platform]);


  const eligibleEntries = useMemo(() => {
    return entries.filter((entry) => {
      if (excludeHidden) {
        const lower = entry.name.toLowerCase();
        if (entry.isHidden || SYSTEM_NAMES.has(lower)) return false;
      }
      if (excludeTiny) {
        const meta = metaCacheRef.current.get(entry.id);
        if (meta?.size !== undefined && meta.size < TINY_FILE_BYTES) {
          return false;
        }
      }
      return true;
    });
  }, [entries, excludeHidden, excludeTiny, metaTick]);

  useEffect(() => {
    const eligibleIds = new Set(eligibleEntries.map((entry) => entry.id));
    setSelectedIds((prev) => new Set([...prev].filter((id) => eligibleIds.has(id))));
    setDeselectedIds((prev) => new Set([...prev].filter((id) => eligibleIds.has(id))));
    if (eligibleIds.size === 0) {
      setAllSelected(false);
    }
  }, [eligibleEntries]);

  const filteredEntries = useMemo(() => {
    const lowerQuery = query.trim().toLowerCase();
    return eligibleEntries.filter((entry) => {
      if (lowerQuery) {
        const haystack = `${entry.name} ${entry.relativePath}`.toLowerCase();
        if (!haystack.includes(lowerQuery)) return false;
      }
      return true;
    });
  }, [eligibleEntries, query]);

  useEffect(() => {
    if (!isOpen) return;
    preScanTokenRef.current += 1;
    const token = preScanTokenRef.current;
    if (!preScanEnabled) {
      setPreScanStatus(null);
      setPreScanProgress(null);
      return;
    }
    const preScanEntries = entries.filter((entry) => {
      if (excludeHidden) {
        const lower = entry.name.toLowerCase();
        if (entry.isHidden || SYSTEM_NAMES.has(lower)) return false;
      }
      if (excludeTiny) {
        const meta = metaCacheRef.current.get(entry.id);
        if (meta?.size !== undefined && meta.size < TINY_FILE_BYTES) {
          return false;
        }
      }
      return true;
    });
    const pendingMeta = getPendingEntries(preScanEntries, ['lastModified', 'size', 'dimensions']);
    const pendingExif = getPendingEntries(preScanEntries, ['exif']);
    const pendingExifTags = metadataReadyRef.current
      ? []
      : getPendingEntries(preScanEntries, ['exifTags']);
    const total = pendingMeta.length + pendingExif.length + pendingExifTags.length;
    preScanProgressRef.current = { total, completed: 0, lastPercent: -1 };
    if (total === 0) {
      setPreScanStatus(null);
      setPreScanProgress(null);
      return;
    }
    setPreScanProgress(0);
    const reportProgress = () => {
      if (preScanTokenRef.current !== token) return;
      const state = preScanProgressRef.current;
      state.completed += 1;
      const percent = Math.min(100, Math.round((state.completed / state.total) * 100));
      if (percent !== state.lastPercent) {
        state.lastPercent = percent;
        setPreScanProgress(percent);
      }
    };
    const run = async () => {
      setPreScanStatus('Pre-scanning file metadata...');
      await runPreScan(preScanEntries, ['lastModified', 'size', 'dimensions'], token, reportProgress);
      if (preScanTokenRef.current !== token) return;
      setPreScanStatus('Pre-scanning EXIF summary...');
      await runPreScan(preScanEntries, ['exif'], token, reportProgress);
      if (preScanTokenRef.current !== token) return;
      if (!metadataReadyRef.current) {
        setPreScanStatus('Pre-scanning EXIF tags...');
        await runPreScan(preScanEntries, ['exifTags'], token, reportProgress);
        if (preScanTokenRef.current !== token) return;
        const ready = preScanEntries.every(
          (entry) => metaCacheRef.current.get(entry.id)?.exifTags !== undefined
        );
        setMetadataReady(ready);
      }
      setPreScanStatus(null);
      setPreScanProgress(null);
    };
    void run();
  }, [entries, excludeHidden, excludeTiny, getPendingEntries, isOpen, preScanEnabled, runPreScan]);

  const viewEntries = useMemo(() => {
    if (!showSelectedOnly) return filteredEntries;
    return filteredEntries.filter((entry) =>
      allSelected ? !deselectedIds.has(entry.id) : selectedIds.has(entry.id)
    );
  }, [allSelected, deselectedIds, filteredEntries, selectedIds, showSelectedOnly]);

  useEffect(() => {
    if (!isOpen) return;
    if (sortBy === 'lastModified') {
      void ensureMetaForEntries(viewEntries, ['lastModified'], 'Preparing sort...');
    }
    if (sortBy === 'size') {
      void ensureMetaForEntries(viewEntries, ['size'], 'Preparing sort...');
    }
  }, [ensureMetaForEntries, isOpen, sortBy, viewEntries]);

  const sortedEntries = useMemo(() => {
    if (sortBy === 'default') return viewEntries;
    const list = [...viewEntries];
    const direction = sortDirection === 'asc' ? 1 : -1;
    const compareText = (a: string, b: string) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }) * direction;
    const compareNumbers = (a: number | null, b: number | null) => {
      if (a === null && b === null) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      return (a - b) * direction;
    };
    list.sort((a, b) => {
      if (sortBy === 'name') {
        return compareText(a.name, b.name);
      }
      if (sortBy === 'path') {
        return compareText(a.relativePath, b.relativePath);
      }
      if (sortBy === 'lastModified') {
        const aVal = metaCacheRef.current.get(a.id)?.lastModified ?? null;
        const bVal = metaCacheRef.current.get(b.id)?.lastModified ?? null;
        return compareNumbers(typeof aVal === 'number' ? aVal : null, typeof bVal === 'number' ? bVal : null);
      }
      if (sortBy === 'size') {
        const aVal = metaCacheRef.current.get(a.id)?.size ?? null;
        const bVal = metaCacheRef.current.get(b.id)?.size ?? null;
        return compareNumbers(typeof aVal === 'number' ? aVal : null, typeof bVal === 'number' ? bVal : null);
      }
      return 0;
    });
    return list;
  }, [metaTick, sortBy, sortDirection, viewEntries]);

  const columns = useMemo(() => {
    if (gridSize.width <= 0) return 1;
    const base = Math.floor((gridSize.width + GRID_GAP) / (COLUMN_MIN + GRID_GAP));
    const minColumns = gridSize.width < 520 ? 2 : 1;
    return Math.max(minColumns, base);
  }, [gridSize.width]);

  const rowHeight = useMemo(() => {
    if (gridSize.width && gridSize.width < 520) {
      return 160;
    }
    return ROW_HEIGHT;
  }, [gridSize.width]);


  const rowCount = Math.ceil(sortedEntries.length / columns);
  const totalHeight = rowCount * rowHeight;

  const overscanRows = platform === 'android' ? 0 : OVERSCAN;
  const visibleRowRange = useMemo(() => {
    const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscanRows);
    const end = Math.min(
      rowCount,
      Math.ceil((scrollTop + gridSize.height) / rowHeight) + overscanRows
    );
    return { start, end };
  }, [scrollTop, rowCount, gridSize.height, rowHeight, overscanRows]);

  const visibleRows = useMemo(() => {
    const rows: number[] = [];
    for (let row = visibleRowRange.start; row < visibleRowRange.end; row += 1) {
      rows.push(row);
    }
    return rows;
  }, [visibleRowRange]);

  const visibleItems = useMemo(() => {
    const items: DirectWriteEntry[] = [];
    visibleRows.forEach((row) => {
      const start = row * columns;
      const rowItems = sortedEntries.slice(start, start + columns);
      items.push(...rowItems);
    });
    return items;
  }, [visibleRows, sortedEntries, columns]);

  useEffect(() => {
    if (!isOpen || !excludeTiny) return;
    if (platform !== 'android') return;
    // DISABLED for Android to prevent contention
    // void ensureMetaForEntriesSilent(visibleItems, ['size']);
  }, [excludeTiny, ensureMetaForEntriesSilent, isOpen, platform, visibleItems]);

  const thumbStatus = useMemo(() => {
    if (visibleItems.length === 0) return null;
    let ready = 0;
    visibleItems.forEach((entry) => {
      const value = thumbMapRef.current.get(entry.id);
      if (!value) return;
      if (typeof value === 'string' && (value.startsWith('STATUS:') || value.startsWith('ERROR:'))) {
        return;
      }
      ready += 1;
    });
    const total = visibleItems.length;
    if (ready >= total) return null;
    return `Loading thumbnails… ${ready}/${total}`;
  }, [thumbTick, visibleItems]);

  const thumbDebugInfo = useMemo(() => {
    if (!debugMode) return null;
    let loading = 0;
    let decoding = 0;
    let error = 0;
    let ready = 0;
    thumbMapRef.current.forEach((value) => {
      if (!value) return;
      if (typeof value !== 'string') {
        ready += 1;
        return;
      }
      if (value.startsWith('STATUS:Loading')) {
        loading += 1;
      } else if (value.startsWith('STATUS:Decoding')) {
        decoding += 1;
      } else if (value.startsWith('ERROR:')) {
        error += 1;
      } else {
        ready += 1;
      }
    });
    return `debug=1 • ready:${ready} loading:${loading} decoding:${decoding} error:${error}`;
  }, [debugMode, thumbTick]);

  const thumbTimingInfo = useMemo(() => {
    if (!debugMode) return null;
    const timings = thumbTimingRef.current;
    const imageDecoderAvailable = typeof (globalThis as any).ImageDecoder === 'function';
    const avg = (total: number, count: number) => (count > 0 ? Math.round(total / count) : 0);
    const loadingAvg = avg(timings.loadingTotalMs, timings.loadingCount);
    const getFileAvg = avg(timings.getFileTotalMs, timings.getFileCount);
    const decodeAvg = avg(timings.decodeTotalMs, timings.decodeCount);
    const decodeOnlyAvg = avg(timings.decodeOnlyTotalMs, timings.decodeOnlyCount);
    const cacheTotal = timings.cacheHit + timings.cacheMiss;
    const cacheRate = cacheTotal > 0 ? Math.round((timings.cacheHit / cacheTotal) * 100) : 0;
    const opfsTotal = timings.opfsHit + timings.opfsMiss;
    const opfsRate = opfsTotal > 0 ? Math.round((timings.opfsHit / opfsTotal) * 100) : 0;
    const exifTotal = timings.exifHit + timings.exifMiss;
    const exifRate = exifTotal > 0 ? Math.round((timings.exifHit / exifTotal) * 100) : 0;
    const exifAvg = avg(timings.exifTotalMs, timings.exifCount);
    const imageDecoderError = timings.imageDecoderLastError
      ? ` err:${timings.imageDecoderLastError}`
      : '';
    const workerError = timings.workerLastError ? ` err:${timings.workerLastError}` : '';
    return `timing(ms) load avg:${loadingAvg} last:${Math.round(timings.loadingLastMs)} • getFile avg:${getFileAvg} last:${Math.round(timings.getFileLastMs)} • decode avg:${decodeAvg} last:${Math.round(timings.decodeLastMs)} • decode-only avg:${decodeOnlyAvg} last:${Math.round(timings.decodeOnlyLastMs)} • cache hit:${timings.cacheHit}/${cacheTotal} (${cacheRate}%) • opfs hit:${timings.opfsHit}/${opfsTotal} (${opfsRate}%) • exif avg:${exifAvg} last:${Math.round(timings.exifLastMs)} hit:${timings.exifHit}/${exifTotal} (${exifRate}%) skip:${timings.exifSkip} • decoder img:${timings.imageDecoderCount} cIB:${timings.createImageBitmapCount} • worker ok:${timings.workerCount} fail:${timings.workerFail}${workerError} • imgDec avail:${imageDecoderAvailable ? 'y' : 'n'} attempt:${timings.imageDecoderAttempt} fail:${timings.imageDecoderFail}${imageDecoderError}`;
  }, [debugMode, thumbTick]);

  const selectedCount = allSelected
    ? Math.max(eligibleEntries.length - deselectedIds.size, 0)
    : selectedIds.size;

  const isMobile = platform === 'android';

  useEffect(() => {
    if (selectedCount === 0 && multiSelectMode) {
      setMultiSelectMode(false);
      setRangeAnchorId(null);
    }
    if (selectedCount === 0 && showSelectedOnly) {
      setShowSelectedOnly(false);
    }
  }, [multiSelectMode, selectedCount, showSelectedOnly]);

  const isSelectedId = useCallback(
    (id: string) => (allSelected ? !deselectedIds.has(id) : selectedIds.has(id)),
    [allSelected, deselectedIds, selectedIds]
  );

  const parseDateInput = useCallback((value: string, endOfDay: boolean) => {
    if (!value) return null;
    const [yearStr, monthStr, dayStr] = value.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (!year || !month || !day) return null;
    return endOfDay
      ? new Date(year, month - 1, day, 23, 59, 59, 999).getTime()
      : new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
  }, []);

  const parseExifDate = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const [datePart] = trimmed.split(' ');
    const [yearStr, monthStr, dayStr] = datePart.split(':');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
  }, []);

  const parseTimeInput = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const [hourStr, minuteStr, secondStr] = trimmed.split(':');
    const hour = Number(hourStr);
    const minute = Number(minuteStr);
    const second = Number(secondStr || 0);
    if (
      Number.isNaN(hour) ||
      Number.isNaN(minute) ||
      Number.isNaN(second) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59 ||
      second < 0 ||
      second > 59
    ) {
      return null;
    }
    return hour * 3600 + minute * 60 + second;
  }, []);

  const parseExifTime = useCallback((value: string) => {
    const match = value.trim().match(/(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?/);
    if (!match) return null;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    const second = Number(match[3] || 0);
    if (
      Number.isNaN(hour) ||
      Number.isNaN(minute) ||
      Number.isNaN(second) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59 ||
      second < 0 ||
      second > 59
    ) {
      return null;
    }
    return hour * 3600 + minute * 60 + second;
  }, []);

  const parseNumericValue = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const fractionMatch = trimmed.match(/(-?\d+(?:\.\d+)?)\s*\/\s*(-?\d+(?:\.\d+)?)/);
    if (fractionMatch) {
      const numerator = Number(fractionMatch[1]);
      const denominator = Number(fractionMatch[2]);
      if (!denominator || Number.isNaN(numerator) || Number.isNaN(denominator)) return null;
      return numerator / denominator;
    }
    const numberMatch = trimmed.match(/-?\d+(?:\.\d+)?/);
    if (!numberMatch) return null;
    const parsed = Number(numberMatch[0]);
    return Number.isNaN(parsed) ? null : parsed;
  }, []);

  const ensureSelected = useCallback(
    (id: string) => {
      if (allSelected) {
        setDeselectedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        return;
      }
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    },
    [allSelected]
  );

  const resolveRangeIds = useCallback(
    (startId: string, endId: string) => {
      if (!sortedEntries.length) return [];
      const startIndex = sortedEntries.findIndex((entry) => entry.id === startId);
      const endIndex = sortedEntries.findIndex((entry) => entry.id === endId);
      if (startIndex < 0 || endIndex < 0) return [];
      const [from, to] = startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
      return sortedEntries.slice(from, to + 1).map((entry) => entry.id);
    },
    [sortedEntries]
  );

  const applyRangeSelection = useCallback(
    (startId: string, endId: string) => {
      const rangeIds = resolveRangeIds(startId, endId);
      if (!rangeIds.length) return;
      if (allSelected) {
        setDeselectedIds((prev) => {
          const next = new Set(prev);
          rangeIds.forEach((id) => next.delete(id));
          return next;
        });
        return;
      }
      setSelectedIds((prev) => {
        const next = new Set(prev);
        rangeIds.forEach((id) => next.add(id));
        return next;
      });
    },
    [allSelected, resolveRangeIds]
  );

  const toggleSelection = (id: string) => {
    if (allSelected) {
      setDeselectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const applySelectionReplace = (ids: Set<string>) => {
    setAllSelected(false);
    setSelectedIds(new Set(ids));
    setDeselectedIds(new Set());
  };

  const applySelectionExclude = (ids: Set<string>) => {
    if (allSelected) {
      setDeselectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const applyRuleReplace = async (
    list: DirectWriteEntry[],
    fields: Array<'size' | 'lastModified' | 'exif' | 'exifTags' | 'dimensions'>,
    label: string,
    predicate: (entry: DirectWriteEntry, meta?: DirectWriteFileMeta) => boolean
  ) => {
    const needsMeta = fields.some(
      (field) =>
        field === 'lastModified' || field === 'exif' || field === 'exifTags' || field === 'dimensions'
    );
    if (needsMeta && !metaFilterActivated) {
      setMetaFilterActivated(true);
    }
    await ensureMetaForEntries(list, fields, label);
    const ids = new Set(
      list.filter((entry) => predicate(entry, metaCacheRef.current.get(entry.id))).map((entry) => entry.id)
    );
    applySelectionReplace(ids);
  };


  const handlePointerDown = (id: string, event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== 'touch') return;
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
    }
    longPressTriggeredRef.current = false;
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      setMultiSelectMode(true);
      setRangeAnchorId(id);
      ensureSelected(id);
    }, LONG_PRESS_MS);
  };

  const handlePointerUp = (id: string, event: React.PointerEvent<HTMLButtonElement>) => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    const isTouch = event.pointerType === 'touch';
    const hasRangeModifier = !isTouch && (event.shiftKey || event.ctrlKey || event.metaKey);

    if (isTouch) {
      if (longPressTriggeredRef.current) return;
      if (multiSelectMode) {
        if (rangeAnchorId && rangeAnchorId !== id) {
          applyRangeSelection(rangeAnchorId, id);
        } else {
          toggleSelection(id);
        }
        setRangeAnchorId(id);
        return;
      }
      toggleSelection(id);
      setRangeAnchorId(id);
      return;
    }

    if (hasRangeModifier) {
      if (!rangeAnchorId) {
        setRangeAnchorId(id);
        ensureSelected(id);
      } else {
        applyRangeSelection(rangeAnchorId, id);
        setRangeAnchorId(id);
      }
      return;
    }

    toggleSelection(id);
    setRangeAnchorId(id);
  };

  const handlePointerLeave = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleSelectToday = async () => {
    const now = Date.now();
    const since = now - 24 * 60 * 60 * 1000;
    await applyRuleReplace(
      eligibleEntries,
      ['lastModified'],
      'Filtering by date...',
      (_entry, meta) => (meta?.lastModified ?? 0) >= since
    );
  };

  const handleSelectLast48h = async () => {
    const now = Date.now();
    const since = now - 48 * 60 * 60 * 1000;
    await applyRuleReplace(
      eligibleEntries,
      ['lastModified'],
      'Filtering by date...',
      (_entry, meta) => (meta?.lastModified ?? 0) >= since
    );
  };

  const handleSelectLast7Days = async () => {
    const now = Date.now();
    const since = now - 7 * 24 * 60 * 60 * 1000;
    await applyRuleReplace(
      eligibleEntries,
      ['lastModified'],
      'Filtering by date...',
      (_entry, meta) => (meta?.lastModified ?? 0) >= since
    );
  };

  const handleSelectLast30Days = async () => {
    const now = Date.now();
    const since = now - 30 * 24 * 60 * 60 * 1000;
    await applyRuleReplace(
      eligibleEntries,
      ['lastModified'],
      'Filtering by date...',
      (_entry, meta) => (meta?.lastModified ?? 0) >= since
    );
  };

  const handleSelectRecentSession = async () => {
    if (!metaFilterActivated) {
      setMetaFilterActivated(true);
    }
    await ensureMetaForEntries(eligibleEntries, ['lastModified'], 'Detecting recent session...');
    const entriesWithMeta = eligibleEntries
      .map((entry) => ({ entry, meta: metaCacheRef.current.get(entry.id) }))
      .filter((item) => typeof item.meta?.lastModified === 'number')
      .sort((a, b) => (a.meta?.lastModified ?? 0) - (b.meta?.lastModified ?? 0));
    if (!entriesWithMeta.length) return;
    const gapLimit = 48 * 60 * 60 * 1000;
    let pivot = 0;
    for (let i = 1; i < entriesWithMeta.length; i += 1) {
      const prev = entriesWithMeta[i - 1].meta?.lastModified ?? 0;
      const curr = entriesWithMeta[i].meta?.lastModified ?? 0;
      if (curr - prev > gapLimit) {
        pivot = i;
      }
    }
    const ids = new Set(entriesWithMeta.slice(pivot).map((item) => item.entry.id));
    applySelectionReplace(ids);
  };

  const handleApplyTimeWindow = async () => {
    const [startHour, startMinute] = timeStart.split(':').map((val) => parseInt(val, 10));
    const [endHour, endMinute] = timeEnd.split(':').map((val) => parseInt(val, 10));
    if (Number.isNaN(startHour) || Number.isNaN(endHour)) return;
    const startTotal = startHour * 60 + (startMinute || 0);
    const endTotal = endHour * 60 + (endMinute || 0);
    const dateStartTs = parseDateInput(dateStart, false);
    const dateEndTs = parseDateInput(dateEnd, true);
    await applyRuleReplace(
      eligibleEntries,
      ['lastModified'],
      'Filtering by time window...',
      (_entry, meta) => {
        const ts = meta?.lastModified ?? 0;
        if (!ts) return false;
        if (dateStartTs !== null && ts < dateStartTs) return false;
        if (dateEndTs !== null && ts > dateEndTs) return false;
        const date = new Date(ts);
        const minutes = date.getHours() * 60 + date.getMinutes();
        if (startTotal <= endTotal) {
          return minutes >= startTotal && minutes <= endTotal;
        }
        return minutes >= startTotal || minutes <= endTotal;
      }
    );
  };

  const handleApplyDateRange = async () => {
    const startTs = parseDateInput(dateStart, false);
    const endTs = parseDateInput(dateEnd, true);
    if (startTs === null && endTs === null) return;
    await applyRuleReplace(
      eligibleEntries,
      ['lastModified'],
      'Filtering by date...',
      (_entry, meta) => {
        const ts = meta?.lastModified ?? 0;
        if (!ts) return false;
        if (startTs !== null && ts < startTs) return false;
        if (endTs !== null && ts > endTs) return false;
        return true;
      }
    );
  };

  const handleExcludeScreenshots = () => {
    const ids = new Set(
      eligibleEntries
        .filter((entry) => {
          const lower = entry.name.toLowerCase();
          return lower.includes('screenshot') || lower.includes('screen shot') || lower.includes('screen-shot');
        })
        .map((entry) => entry.id)
    );
    applySelectionExclude(ids);
  };

  const handleExcludeCopies = () => {
    const ids = new Set(
      eligibleEntries
        .filter((entry) => {
          const lower = entry.name.toLowerCase();
          return lower.includes('_copy') || lower.includes(' copy') || lower.endsWith('-copy.jpg');
        })
        .map((entry) => entry.id)
    );
    applySelectionExclude(ids);
  };

  const handleSelectNoExif = async () => {
    await applyRuleReplace(
      eligibleEntries,
      ['exif'],
      'Checking EXIF...',
      (_entry, meta) => !meta?.hasExif
    );
  };

  const handleSelectNoGps = async () => {
    await applyRuleReplace(
      eligibleEntries,
      ['exif'],
      'Checking GPS metadata...',
      (_entry, meta) => !meta?.hasGps
    );
  };

  const handleSelectLandscape = async () => {
    await applyRuleReplace(
      eligibleEntries,
      ['dimensions'],
      'Checking orientation...',
      (_entry, meta) => (meta?.width ?? 0) > (meta?.height ?? 0)
    );
  };

  const handleSelectPortrait = async () => {
    await applyRuleReplace(
      eligibleEntries,
      ['dimensions'],
      'Checking orientation...',
      (_entry, meta) => (meta?.height ?? 0) > (meta?.width ?? 0)
    );
  };

  const handleApplyNameFilters = () => {
    const include = nameInclude.trim().toLowerCase();
    const exclude = nameExclude.trim().toLowerCase();
    if (!include && !exclude) return;
    const ids = new Set(
      eligibleEntries
        .filter((entry) => {
          const haystack = `${entry.name} ${entry.relativePath}`.toLowerCase();
          if (include && !haystack.includes(include)) return false;
          if (exclude && haystack.includes(exclude)) return false;
          return true;
        })
        .map((entry) => entry.id)
    );
    applySelectionReplace(ids);
  };

  const handleApplyRegexFilter = () => {
    const pattern = regexFilter.trim();
    if (!pattern) return;
    try {
      const regex = new RegExp(pattern, 'i');
      const ids = new Set(
        eligibleEntries
          .filter((entry) => regex.test(`${entry.name} ${entry.relativePath}`))
          .map((entry) => entry.id)
      );
      applySelectionReplace(ids);
      setRuleStatus(null);
    } catch {
      setRuleStatus('Invalid regex pattern.');
    }
  };

  const handleApplyRangeFilter = () => {
    const prefix = rangePrefix.trim();
    const startVal = parseInt(rangeStart, 10);
    const endVal = parseInt(rangeEnd, 10);
    if (!prefix || Number.isNaN(startVal) || Number.isNaN(endVal)) return;
    const min = Math.min(startVal, endVal);
    const max = Math.max(startVal, endVal);
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`${escaped}(\\d+)`, 'i');
    const ids = new Set(
      eligibleEntries
        .filter((entry) => {
          const match = entry.name.match(regex);
          if (!match) return false;
          const num = parseInt(match[1], 10);
          return !Number.isNaN(num) && num >= min && num <= max;
        })
        .map((entry) => entry.id)
    );
    applySelectionReplace(ids);
  };

  const handleApplyMetadataFilter = async () => {
    const isDateField = DATE_METADATA_FIELDS.has(metadataField);
    const isTimeField = TIME_METADATA_FIELDS.has(metadataField);
    const isNumericField = NUMERIC_METADATA_FIELDS.has(metadataField);
    if (isDateField) {
      const startTs = parseDateInput(metadataDateStart, false);
      const endTs = parseDateInput(metadataDateEnd, true);
      if (startTs === null && endTs === null) return;
      await applyRuleReplace(
        eligibleEntries,
        ['exifTags'],
        'Filtering by metadata...',
        (_entry, meta) => {
          const raw = meta?.exifTags?.[metadataField];
          if (!raw) return false;
          const ts = parseExifDate(String(raw));
          if (ts === null) return false;
          if (startTs !== null && ts < startTs) return false;
          if (endTs !== null && ts > endTs) return false;
          return true;
        }
      );
      return;
    }
    if (isTimeField) {
      const startSec = parseTimeInput(metadataTimeStart);
      const endSec = parseTimeInput(metadataTimeEnd);
      if (startSec === null && endSec === null) return;
      await applyRuleReplace(
        eligibleEntries,
        ['exifTags'],
        'Filtering by metadata...',
        (_entry, meta) => {
          const raw = meta?.exifTags?.[metadataField];
          if (!raw) return false;
          const value = parseExifTime(String(raw));
          if (value === null) return false;
          if (startSec !== null && endSec !== null) {
            if (startSec <= endSec) {
              return value >= startSec && value <= endSec;
            }
            return value >= startSec || value <= endSec;
          }
          if (startSec !== null && value < startSec) return false;
          if (endSec !== null && value > endSec) return false;
          return true;
        }
      );
      return;
    }
    if (isNumericField) {
      const min = metadataRangeMin.trim() === '' ? null : Number(metadataRangeMin);
      const max = metadataRangeMax.trim() === '' ? null : Number(metadataRangeMax);
      if (min === null && max === null) return;
      if ((min !== null && Number.isNaN(min)) || (max !== null && Number.isNaN(max))) return;
      await applyRuleReplace(
        eligibleEntries,
        ['exifTags'],
        'Filtering by metadata...',
        (_entry, meta) => {
          const raw = meta?.exifTags?.[metadataField];
          if (!raw) return false;
          const value = parseNumericValue(String(raw));
          if (value === null) return false;
          if (min !== null && value < min) return false;
          if (max !== null && value > max) return false;
          return true;
        }
      );
      return;
    }
    const value = metadataValue.trim().toLowerCase();
    if (!value) return;
    await applyRuleReplace(
      eligibleEntries,
      ['exifTags'],
      'Filtering by metadata...',
      (_entry, meta) => {
        const raw = meta?.exifTags?.[metadataField];
        if (!raw) return false;
        return String(raw).toLowerCase().includes(value);
      }
    );
  };

  const handleSelectAll = () => {
    setAllSelected(true);
    setSelectedIds(new Set());
    setDeselectedIds(new Set());
    setRangeAnchorId(null);
  };

  const handleClearSelection = () => {
    setAllSelected(false);
    setSelectedIds(new Set());
    setDeselectedIds(new Set());
    setRangeAnchorId(null);
    setMultiSelectMode(false);
  };

  const handleConfirm = () => {
    if (allSelected) {
      const ids = eligibleEntries
        .filter((entry) => !deselectedIds.has(entry.id))
        .map((entry) => entry.id);
      onConfirm({
        allSelected: false,
        selectedIds: ids,
        excludedIds: [],
      });
      return;
    }
    onConfirm({
      allSelected: false,
      selectedIds: Array.from(selectedIds),
      excludedIds: [],
    });
  };

  const makeThumbCacheKey = useCallback(
    (entry: DirectWriteEntry) =>
      `${entry.id}::${highQualityThumbs ? 'hq' : 'lq'}::${thumbProfile.thumbSize}::${THUMB_CACHE_VERSION}`,
    [highQualityThumbs, thumbProfile.thumbSize]
  );

  const makeThumbCacheKeyForSize = useCallback(
    (entry: DirectWriteEntry, size: number) =>
      `${entry.id}::${highQualityThumbs ? 'hq' : 'lq'}::${size}::${THUMB_CACHE_VERSION}`,
    [highQualityThumbs]
  );

  const encodeThumbBitmap = useCallback(
    async (bitmap: ImageBitmap, sizeOverride?: number) => {
      const size = sizeOverride ?? thumbProfile.thumbSize;
      const canvas =
        typeof OffscreenCanvas !== 'undefined'
          ? new OffscreenCanvas(size, size)
          : Object.assign(document.createElement('canvas'), {
            width: size,
            height: size,
          });
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) return null;
      if ('imageSmoothingQuality' in ctx) {
        ctx.imageSmoothingQuality = highQualityThumbs ? 'high' : 'low';
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      const scale = Math.min(size / bitmap.width, size / bitmap.height, 1);
      const targetWidth = Math.max(1, Math.round(bitmap.width * scale));
      const targetHeight = Math.max(1, Math.round(bitmap.height * scale));
      const offsetX = Math.floor((size - targetWidth) / 2);
      const offsetY = Math.floor((size - targetHeight) / 2);
      ctx.drawImage(bitmap, offsetX, offsetY, targetWidth, targetHeight);

      if ('convertToBlob' in canvas) {
        return await (canvas as OffscreenCanvas).convertToBlob({
          type: THUMB_FORMAT,
          quality: thumbProfile.quality,
        });
      }
      return await new Promise<Blob | null>((resolve) => {
        (canvas as HTMLCanvasElement).toBlob(resolve, THUMB_FORMAT, thumbProfile.quality);
      });
    },
    [highQualityThumbs, thumbProfile.quality, thumbProfile.thumbSize]
  );

  const scheduleIdle = useCallback((handler: () => void) => {
    if (typeof window === 'undefined') return -1;
    const anyWindow = window as typeof window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof anyWindow.requestIdleCallback === 'function') {
      return anyWindow.requestIdleCallback(handler, { timeout: 700 });
    }
    return window.setTimeout(handler, 120);
  }, []);

  const cancelIdle = useCallback((handle: number) => {
    if (handle < 0 || typeof window === 'undefined') return;
    const anyWindow = window as typeof window & {
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof anyWindow.cancelIdleCallback === 'function') {
      anyWindow.cancelIdleCallback(handle);
      return;
    }
    window.clearTimeout(handle);
  }, []);

  const getThumbWorker = useCallback(() => {
    if (thumbWorkerRef.current) return thumbWorkerRef.current;
    if (typeof Worker === 'undefined') return null;
    try {
      const worker = new Worker(
        new URL('../workers/thumbDecodeWorker.ts', import.meta.url),
        { type: 'module' }
      );
      worker.onmessage = (event: MessageEvent<{ id: number; bitmap?: ImageBitmap; error?: string }>) => {
        const { id, bitmap, error } = event.data || {};
        const pending = thumbWorkerPendingRef.current.get(id);
        if (!pending) return;
        thumbWorkerPendingRef.current.delete(id);
        if (typeof window !== 'undefined' && typeof pending.timeout === 'number') {
          window.clearTimeout(pending.timeout);
        }
        if (error) {
          pending.reject(new Error(error));
          return;
        }
        pending.resolve(bitmap ?? null);
      };
      worker.onerror = (event) => {
        const err = new Error((event as ErrorEvent).message || 'worker error');
        thumbWorkerPendingRef.current.forEach(({ resolve, timeout }) => {
          if (typeof window !== 'undefined' && typeof timeout === 'number') {
            window.clearTimeout(timeout);
          }
          resolve(null);
        });
        thumbWorkerPendingRef.current.clear();
        thumbTimingRef.current.workerFail += 1;
        thumbTimingRef.current.workerLastError = err.message;
      };
      thumbWorkerRef.current = worker;
      return worker;
    } catch {
      return null;
    }
  }, []);

  const decodeThumbWithWorker = useCallback(
    async (file: File, resizeQuality: ImageSmoothingQuality, sizeOverride?: number) => {
      const worker = getThumbWorker();
      if (!worker) return null;
      const requestId = (thumbWorkerRequestRef.current += 1);
      try {
        return await new Promise<ImageBitmap | null>((resolve, reject) => {
          const timeout =
            typeof window !== 'undefined'
              ? window.setTimeout(() => {
                  thumbWorkerPendingRef.current.delete(requestId);
                  resolve(null);
                }, 20000)
              : undefined;
          thumbWorkerPendingRef.current.set(requestId, { resolve, reject, timeout });
          worker.postMessage({
            id: requestId,
            file,
            size: sizeOverride ?? thumbProfile.thumbSize,
            resizeQuality,
            imageOrientation: 'from-image',
          });
        });
      } catch (err: any) {
        const timing = thumbTimingRef.current;
        timing.workerFail += 1;
        timing.workerLastError = String(err?.message || err || 'worker failed');
        return null;
      }
    },
    [getThumbWorker, thumbProfile.thumbSize]
  );

  const buildThumbBitmap = useCallback(
    async (file: File, sizeOverride?: number): Promise<ThumbBitmapResult> => {
      if (typeof createImageBitmap !== 'function') {
        return { bitmap: null, decoder: null };
      }

      const resizeQuality =
        highQualityThumbs ? 'high' : platform === 'android' ? 'low' : 'medium';
      const isAndroid = platform === 'android';
      if (isAndroid) {
        const workerBitmap = await decodeThumbWithWorker(file, resizeQuality, sizeOverride);
        if (workerBitmap) {
          return { bitmap: workerBitmap, decoder: 'worker' };
        }
      }
      const ImageDecoderCtor = !isAndroid ? (globalThis as any).ImageDecoder : null;
      if (ImageDecoderCtor) {
        let decoder: any;
        let decodedImage: any;
        try {
          thumbTimingRef.current.imageDecoderAttempt += 1;
          const decoderData =
            typeof file.stream === 'function' ? file.stream() : await file.arrayBuffer();
          decoder = new ImageDecoderCtor({
            data: decoderData,
            type: file.type || 'image/jpeg',
          });
          let result: any;
          let usedDecoderResize = false;
          const targetSize = sizeOverride ?? thumbProfile.thumbSize;
          try {
            result = await decoder.decode({
              frameIndex: 0,
              completeFramesOnly: true,
              resizeWidth: targetSize,
            });
            usedDecoderResize = true;
          } catch {
            result = await decoder.decode({ frameIndex: 0, completeFramesOnly: true });
          }
          decodedImage = result?.image;
          const bitmap = usedDecoderResize
            ? await createImageBitmap(decodedImage)
            : await createImageBitmap(decodedImage, {
                resizeWidth: targetSize,
                resizeQuality,
              });
          if (decodedImage?.close) {
            decodedImage.close();
          }
          if (decoder?.close) {
            decoder.close();
          }
          return { bitmap, decoder: 'imageDecoder' };
        } catch (err: any) {
          thumbTimingRef.current.imageDecoderFail += 1;
          thumbTimingRef.current.imageDecoderLastError = String(err?.message || err || 'unknown');
          if (decodedImage?.close) {
            decodedImage.close();
          }
          if (decoder?.close) {
            decoder.close();
          }
        }
      }

      try {
        const targetSize = sizeOverride ?? thumbProfile.thumbSize;
        return {
          bitmap: await createImageBitmap(file, {
            resizeWidth: targetSize,
            resizeQuality,
            imageOrientation: 'from-image',
          }),
          decoder: 'createImageBitmap',
        };
      } catch {
        try {
          return {
            bitmap: await createImageBitmap(file, { imageOrientation: 'from-image' }),
            decoder: 'createImageBitmap',
          };
        } catch {
          return { bitmap: null, decoder: null };
        }
      }
    },
    [decodeThumbWithWorker, highQualityThumbs, platform, thumbProfile.thumbSize]
  );

  const loadThumb = useCallback(
    async (
      entry: DirectWriteEntry,
      generation: number,
      options: { sizeOverride?: number; cacheKeyOverride?: string } = {}
    ) => {
      const now = () =>
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now();
      const isAndroid = platform === 'android';
      const sizeOverride = options.sizeOverride;
      const useCache = true;
      const thumbs = thumbMapRef.current;
      if (!visibleIdsRef.current.has(entry.id)) return;
      if (loadingThumbsRef.current.has(entry.id)) return;
      loadingThumbsRef.current.add(entry.id);
      const existingThumb = thumbs.get(entry.id);
      const requestedSize = sizeOverride ?? thumbProfile.thumbSize;
      const existingSize = thumbSizeRef.current.get(entry.id);
      if (
        existingThumb &&
        typeof existingSize === 'number' &&
        existingSize >= requestedSize
      ) {
        loadingThumbsRef.current.delete(entry.id);
        return;
      }
      const clearStatus = () => {
        const existing = thumbs.get(entry.id);
      if (typeof existing === 'string' && existing.startsWith('STATUS:')) {
        thumbs.delete(entry.id);
      }
      };
      const storeThumb = (value: ThumbValue, sizeValue: number) => {
        thumbs.set(entry.id, value);
        thumbSizeRef.current.set(entry.id, sizeValue);
        thumbOrderRef.current.push(entry.id);
        while (thumbs.size > thumbProfile.maxThumbs) {
          const oldest = thumbOrderRef.current.shift();
          if (oldest === undefined) break;
          const oldThumb = thumbs.get(oldest as string);
          if (oldThumb) {
            if (typeof oldThumb === 'string') {
              if (!oldThumb.startsWith('STATUS:') && !oldThumb.startsWith('ERROR:')) {
                URL.revokeObjectURL(oldThumb);
              }
            } else {
              oldThumb.close();
            }
          }
          thumbs.delete(oldest as string);
          thumbSizeRef.current.delete(oldest as string);
        }
        forceThumbTick((prev) => prev + 1);
      };
      try {
        if (!existingThumb || typeof existingThumb === 'string') {
          thumbs.set(entry.id, 'STATUS:Loading...');
        }
        forceThumbTick(n => n + 1);
        const loadStart = now();

        const cacheKey = options.cacheKeyOverride || makeThumbCacheKey(entry);
        if (useCache) {
          const cached = await getCachedThumb(cacheKey);
          if (thumbGenerationRef.current !== generation || !visibleIdsRef.current.has(entry.id)) {
            clearStatus();
            return;
          }
          if (cached?.blob) {
            const loadingMs = now() - loadStart;
            const timings = thumbTimingRef.current;
            timings.loadingTotalMs += loadingMs;
            timings.loadingCount += 1;
            timings.loadingLastMs = loadingMs;
            timings.cacheHit += 1;

            const cachedUrl = URL.createObjectURL(cached.blob);
            if (thumbGenerationRef.current !== generation || !visibleIdsRef.current.has(entry.id)) {
              URL.revokeObjectURL(cachedUrl);
              clearStatus();
              return;
            }
            storeThumb(cachedUrl, requestedSize);
            return;
          }
        }

        if (isAndroid && opfsThumbsEnabled && canUseOpfsThumbs()) {
          const opfsBlob = await getOpfsThumb(cacheKey);
          if (thumbGenerationRef.current !== generation || !visibleIdsRef.current.has(entry.id)) {
            clearStatus();
            return;
          }
          if (opfsBlob) {
            const loadingMs = now() - loadStart;
            const timings = thumbTimingRef.current;
            timings.loadingTotalMs += loadingMs;
            timings.loadingCount += 1;
            timings.loadingLastMs = loadingMs;
            timings.opfsHit += 1;

            const opfsUrl = URL.createObjectURL(opfsBlob);
            if (thumbGenerationRef.current !== generation || !visibleIdsRef.current.has(entry.id)) {
              URL.revokeObjectURL(opfsUrl);
              clearStatus();
              return;
            }
            storeThumb(opfsUrl, requestedSize);
            return;
          }
          thumbTimingRef.current.opfsMiss += 1;
        }

        let file: File | undefined;
        let directError: any = null;
        const getFileStart = now();
        try {
          file = await entry.handle.getFile();
        } catch (err) {
          directError = err;
        }

        if (!file && entry.parentHandle) {
          try {
            const refreshed = await entry.parentHandle.getFileHandle(entry.name);
            file = await refreshed.getFile();
          } catch (err) {
            if (!directError) {
              directError = err;
            }
          }
        }

        if (!file) {
          throw directError;
        }

        const getFileMs = now() - getFileStart;
        const loadingMs = now() - loadStart;
        const timings = thumbTimingRef.current;
        timings.getFileTotalMs += getFileMs;
        timings.getFileCount += 1;
        timings.getFileLastMs = getFileMs;
        timings.loadingTotalMs += loadingMs;
        timings.loadingCount += 1;
        timings.loadingLastMs = loadingMs;
        if (useCache) {
          timings.cacheMiss += 1;
        }

        if (thumbGenerationRef.current !== generation || !visibleIdsRef.current.has(entry.id)) {
          clearStatus();
          return;
        }

        if (!existingThumb || typeof existingThumb === 'string') {
          thumbs.set(entry.id, 'STATUS:Decoding...');
        }
        forceThumbTick(n => n + 1);
        const decodeStart = now();
        let thumbBlob: Blob | null = null;
        let thumbBitmap: ImageBitmap | null = null;
        const attemptedExif = entry.extension === 'jpg' || entry.extension === 'jpeg';
        const allowExif =
          attemptedExif && !PREFER_FULL_THUMBS && !isAndroid && !exifAdaptiveRef.current.disabled;
        if (allowExif) {
          const exifStart = now();
          const scanBytes = isAndroid ? EXIF_THUMB_SCAN_BYTES_ANDROID : EXIF_THUMB_SCAN_BYTES;
          exifAdaptiveRef.current.attempts += 1;
          try {
            thumbBlob = await readEmbeddedJpegThumb(file, scanBytes);
          } catch {
            thumbBlob = null;
          }
          const exifMs = now() - exifStart;
          const exifTiming = thumbTimingRef.current;
          exifTiming.exifTotalMs += exifMs;
          exifTiming.exifCount += 1;
          exifTiming.exifLastMs = exifMs;
          if (isAndroid && exifMs > EXIF_ADAPTIVE_SLOW_MS) {
            exifAdaptiveRef.current.slowCount += 1;
            if (exifAdaptiveRef.current.slowCount >= EXIF_ADAPTIVE_SLOW_LIMIT) {
              exifAdaptiveRef.current.disabled = true;
            }
          }
        }
        if (allowExif) {
          if (thumbBlob) {
            thumbTimingRef.current.exifHit += 1;
          } else {
            thumbTimingRef.current.exifMiss += 1;
          }
        } else if (attemptedExif) {
          thumbTimingRef.current.exifSkip += 1;
        }
        if (!thumbBlob) {
          const decodeOnlyStart = now();
          const result = await buildThumbBitmap(file, sizeOverride);
          const decodeOnlyMs = now() - decodeOnlyStart;
          const decodeOnlyTimings = thumbTimingRef.current;
          decodeOnlyTimings.decodeOnlyTotalMs += decodeOnlyMs;
          decodeOnlyTimings.decodeOnlyCount += 1;
          decodeOnlyTimings.decodeOnlyLastMs = decodeOnlyMs;
          if (result.decoder === 'imageDecoder') {
            decodeOnlyTimings.imageDecoderCount += 1;
          } else if (result.decoder === 'createImageBitmap') {
            decodeOnlyTimings.createImageBitmapCount += 1;
          } else if (result.decoder === 'worker') {
            decodeOnlyTimings.workerCount += 1;
          }
          thumbBitmap = result.bitmap;
        }
        const decodeMs = now() - decodeStart;
        const decodeTimings = thumbTimingRef.current;
        decodeTimings.decodeTotalMs += decodeMs;
        decodeTimings.decodeCount += 1;
        decodeTimings.decodeLastMs = decodeMs;

        if (thumbGenerationRef.current !== generation || !visibleIdsRef.current.has(entry.id)) {
          thumbBitmap?.close();
          clearStatus();
          return;
        }

        if (thumbBlob) {
          const embeddedBlob = thumbBlob;
          const thumbUrl = URL.createObjectURL(embeddedBlob);
          if (thumbGenerationRef.current !== generation || !visibleIdsRef.current.has(entry.id)) {
            URL.revokeObjectURL(thumbUrl);
            clearStatus();
            return;
          }
          storeThumb(thumbUrl, requestedSize);
          if (useCache && !thumbCacheWritingRef.current.has(cacheKey)) {
            thumbCacheWritingRef.current.add(cacheKey);
            const persist = () => {
              void saveCachedThumb(cacheKey, requestedSize, file.lastModified, embeddedBlob).finally(() => {
                thumbCacheWritingRef.current.delete(cacheKey);
              });
            };
            const handle = scheduleIdle(persist);
            if (handle < 0) {
              persist();
            }
          }
          if (isAndroid && opfsThumbsEnabled && canUseOpfsThumbs()) {
            const persistOpfs = () => {
              void saveOpfsThumb(cacheKey, embeddedBlob);
            };
            const handle = scheduleIdle(persistOpfs);
            if (handle < 0) {
              persistOpfs();
            }
          }
          return;
        }

        if (!thumbBitmap) {
          const fallbackUrl = URL.createObjectURL(file);
          if (thumbGenerationRef.current !== generation || !visibleIdsRef.current.has(entry.id)) {
            URL.revokeObjectURL(fallbackUrl);
            clearStatus();
            return;
          }
          storeThumb(fallbackUrl, requestedSize);
          return;
        }

        storeThumb(thumbBitmap, requestedSize);
        if (useCache && !thumbCacheWritingRef.current.has(cacheKey)) {
          thumbCacheWritingRef.current.add(cacheKey);
          const persist = async () => {
            try {
              if (thumbMapRef.current.get(entry.id) !== thumbBitmap) {
                return;
              }
              const blob = await encodeThumbBitmap(thumbBitmap, requestedSize);
              if (blob) {
                await saveCachedThumb(cacheKey, requestedSize, file.lastModified, blob);
                const current = thumbMapRef.current.get(entry.id);
                if (current === thumbBitmap) {
                  const url = URL.createObjectURL(blob);
                  thumbMapRef.current.set(entry.id, url);
                  thumbBitmap?.close();
                  forceThumbTick((prev) => prev + 1);
                }
              }
            } finally {
              thumbCacheWritingRef.current.delete(cacheKey);
            }
          };
          const handle = scheduleIdle(() => {
            void persist();
          });
          if (handle < 0) {
            void persist();
          }
        }
        if (isAndroid && opfsThumbsEnabled && canUseOpfsThumbs()) {
          const persistOpfs = async () => {
            if (thumbMapRef.current.get(entry.id) !== thumbBitmap) return;
            const blob = await encodeThumbBitmap(thumbBitmap, requestedSize);
            if (blob) {
              await saveOpfsThumb(cacheKey, blob);
            }
          };
          const handle = scheduleIdle(() => {
            void persistOpfs();
          });
          if (handle < 0) {
            void persistOpfs();
          }
        }
      } catch (err: any) {
        thumbs.set(entry.id, `ERROR:${err?.message || 'Fail'}`);
        forceThumbTick((prev) => prev + 1);
      } finally {
        loadingThumbsRef.current.delete(entry.id);
      }
    },
    [
      buildThumbBitmap,
      encodeThumbBitmap,
      makeThumbCacheKey,
      makeThumbCacheKeyForSize,
      opfsThumbsEnabled,
      platform,
      scheduleIdle,
      thumbProfile.maxThumbs,
      thumbProfile.thumbSize,
    ]
  );

  const pumpThumbQueue = useCallback(() => {
    if (!isOpen) return;
    if (platform === 'android' && isUserScrollingRef.current) {
      scheduleIdle(() => {
        pumpThumbQueue();
      });
      return;
    }
    const processQueue = async () => {
      while (
        thumbActiveRef.current < thumbProfile.decodeConcurrency &&
        thumbQueueRef.current.length > 0
      ) {
        const next = thumbQueueRef.current.shift();
        if (!next) break;
        thumbQueuedRef.current.delete(next.id);
        thumbActiveRef.current += 1;
        const generation = thumbGenerationRef.current;
        const isAndroid = platform === 'android';
        const useFastPreview = isAndroid && fastPreviewEnabled;
        const previewSize = useFastPreview ? Math.max(96, Math.round(thumbProfile.thumbSize * 0.45)) : undefined;
        const cacheKeyOverride = useFastPreview
          ? makeThumbCacheKeyForSize(next, previewSize!)
          : undefined;

        loadThumb(next, generation, {
          sizeOverride: previewSize,
          cacheKeyOverride,
        }).finally(() => {
          if (thumbGenerationRef.current === generation) {
            thumbActiveRef.current = Math.max(0, thumbActiveRef.current - 1);
            pumpThumbQueue();
          }
        });
      }
    };
    void processQueue();
  }, [
    isOpen,
    loadThumb,
    thumbProfile.decodeConcurrency,
    platform,
    fastPreviewEnabled,
    thumbProfile.thumbSize,
    makeThumbCacheKeyForSize,
    scheduleIdle,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const isAndroid = platform === 'android';
    if (!isAndroid || !fastPreviewEnabled) return;
    const generation = thumbGenerationRef.current;
    const upgradeSize = thumbProfile.thumbSize;

    const handle = scheduleIdle(() => {
      if (!isOpen || thumbGenerationRef.current !== generation) return;
      visibleItems.forEach((entry) => {
        if (loadingThumbsRef.current.has(entry.id)) return;
        if (!visibleIdsRef.current.has(entry.id)) return;
        const cacheKeyOverride = makeThumbCacheKeyForSize(entry, upgradeSize);
        loadThumb(entry, generation, {
          sizeOverride: upgradeSize,
          cacheKeyOverride,
        });
      });
    });
    return () => cancelIdle(handle);
  }, [
    cancelIdle,
    fastPreviewEnabled,
    isOpen,
    loadThumb,
    makeThumbCacheKeyForSize,
    platform,
    scheduleIdle,
    thumbProfile.thumbSize,
    visibleItems,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const visibleIds = new Set(visibleItems.map((entry) => entry.id));
    visibleIdsRef.current = visibleIds;

    const nextQueue: DirectWriteEntry[] = [];
    visibleItems.forEach((entry) => {
      if (thumbMapRef.current.has(entry.id)) return;
      if (loadingThumbsRef.current.has(entry.id)) return;
      nextQueue.push(entry);
    });

    if (platform === 'android' && fastPreviewEnabled) {
      thumbQueueRef.current = nextQueue.slice(0, Math.min(nextQueue.length, columns));
      thumbQueuedRef.current = new Set(thumbQueueRef.current.map((entry) => entry.id));
    } else {
      thumbQueueRef.current = nextQueue;
      thumbQueuedRef.current = new Set(nextQueue.map((entry) => entry.id));
    }
    pumpThumbQueue();

    const generation = thumbGenerationRef.current;
    const idleHandle = scheduleIdle(() => {
      if (!isOpen || thumbGenerationRef.current !== generation) return;
      const prefetchStart = Math.max(0, visibleRowRange.start - thumbProfile.prefetchRows);
      const prefetchEnd = Math.min(rowCount, visibleRowRange.end + thumbProfile.prefetchRows);
      const prefetchItems: DirectWriteEntry[] = [];
      for (let row = prefetchStart; row < prefetchEnd; row += 1) {
        if (row >= visibleRowRange.start && row < visibleRowRange.end) continue;
        const start = row * columns;
        const rowItems = sortedEntries.slice(start, start + columns);
        prefetchItems.push(...rowItems);
      }

      prefetchItems.forEach((entry) => {
        if (visibleIds.has(entry.id)) return;
        if (thumbMapRef.current.has(entry.id)) return;
        if (loadingThumbsRef.current.has(entry.id)) return;
        if (thumbQueuedRef.current.has(entry.id)) return;
        thumbQueuedRef.current.add(entry.id);
        thumbQueueRef.current.push(entry);
      });
      pumpThumbQueue();
    });

    return () => cancelIdle(idleHandle);
  }, [
    cancelIdle,
    columns,
    isOpen,
    pumpThumbQueue,
    rowCount,
    scheduleIdle,
    sortedEntries,
    thumbProfile.prefetchRows,
    visibleItems,
    visibleRowRange.end,
    visibleRowRange.start,
  ]);

  const showMetaFilterNote = entries.length > 100;
  const isDesktopAdvanced = showAdvanced && !isMobile;
  const showAdvancedDrawer = showAdvanced && (isMobile || (isDesktopAdvanced && !useSidePanel));
  const metadataFieldLabel = useMemo(
    () => METADATA_FIELDS.find((field) => field.value === metadataField)?.label ?? 'Select field',
    [metadataField]
  );

  const updateSidePanelFit = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!isDesktopAdvanced) {
      setUseSidePanel(false);
      return;
    }
    if (window.innerWidth < 1200) {
      setUseSidePanel(false);
      return;
    }
    const panelWidth = Math.min(Math.max(window.innerWidth * 0.28, 280), 380);
    const gap = 16;
    const shell = pickerRef.current?.closest('.modal-shell') as HTMLElement | null;
    if (!shell) {
      setUseSidePanel(false);
      return;
    }
    const rect = shell.getBoundingClientRect();
    setUseSidePanel(rect.left - gap - panelWidth >= 0);
  }, [isDesktopAdvanced]);

  useEffect(() => {
    updateSidePanelFit();
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', updateSidePanelFit);
    return () => window.removeEventListener('resize', updateSidePanelFit);
  }, [updateSidePanelFit]);

  const advancedSections = (
    <>
      {isMobile && (
        <div className="photo-picker__section">
          <div className="photo-picker__section-title">Search</div>
          <div className="photo-picker__search">
            <input
              type="search"
              placeholder="Search by name or path"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        </div>
      )}
      <div className="photo-picker__section">
        <div className="photo-picker__section-title">Safety & cleanup</div>
        <label className="photo-picker__toggle">
          <input
            type="checkbox"
            checked={excludeHidden}
            onChange={(event) => setExcludeHidden(event.target.checked)}
          />
          <span>Exclude hidden/system files</span>
        </label>
        <label className="photo-picker__toggle">
          <input
            type="checkbox"
            checked={excludeTiny}
            onChange={(event) => setExcludeTiny(event.target.checked)}
          />
          <span>Exclude tiny files (&lt; 5 KB)</span>
        </label>
        <label className="photo-picker__toggle">
          <input
            type="checkbox"
            checked={highQualityThumbs}
            onChange={(event) => setHighQualityThumbs(event.target.checked)}
          />
          <span>High quality thumbnails</span>
        </label>
        <p className="photo-picker__toggle-hint">Sharper previews, higher CPU/RAM usage.</p>
        {platform === 'android' && (
          <>
            <label className="photo-picker__toggle">
              <input
                type="checkbox"
                checked={opfsThumbsEnabled}
                onChange={(event) => setOpfsThumbsEnabled(event.target.checked)}
                disabled={!canUseOpfsThumbs()}
              />
              <span>Cache thumbnails in device storage (faster next time)</span>
            </label>
            {!canUseOpfsThumbs() && (
              <p className="photo-picker__toggle-hint">
                Not available in this browser or context.
              </p>
            )}
          </>
        )}
        {platform === 'android' && (
          <label className="photo-picker__toggle">
            <input
              type="checkbox"
              checked={fastPreviewEnabled}
              onChange={(event) => setFastPreviewEnabled(event.target.checked)}
            />
            <span>Fast preview on Android (low-res first)</span>
          </label>
        )}
        <label className="photo-picker__toggle">
          <input
            type="checkbox"
            checked={preScanEnabled}
            onChange={(event) => setPreScanEnabled(event.target.checked)}
          />
          <span>Pre-scan metadata now</span>
        </label>
        {preScanEnabled && entries.length > 100 && (
          <p className="photo-picker__toggle-hint">
            Pre-scanning metadata on large folders can take several minutes.
          </p>
        )}
        <button
          type="button"
          className="photo-picker__chip"
          onClick={handleClearThumbCache}
          disabled={clearingThumbs}
        >
          {clearingThumbs ? 'Clearing thumbnail cache...' : 'Clear thumbnail cache'}
        </button>
      </div>

      <div className="photo-picker__section">
        <div className="photo-picker__section-title">Sorting</div>
        <div className="photo-picker__name-row">
          <select
            className="photo-picker__select"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value as (typeof SORT_OPTIONS)[number]['value'])}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="photo-picker__chip"
            onClick={() => setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
          >
            {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
          </button>
        </div>
      </div>

      <div className="photo-picker__section">
        <div className="photo-picker__section-title">Metadata filters</div>
        <div className="photo-picker__name-row">
          <div className="photo-picker__dropdown">
            <button
              type="button"
              className="photo-picker__select photo-picker__select-btn"
              onClick={() => setMetadataMenuOpen((prev) => !prev)}
              aria-expanded={metadataMenuOpen}
            >
              {metadataFieldLabel}
            </button>
            {metadataMenuOpen && (
              <div className="photo-picker__dropdown-menu" role="listbox">
                {METADATA_FIELDS.map((field) => {
                  const isActive = field.value === metadataField;
                  return (
                    <button
                      key={field.value}
                      type="button"
                      className={`photo-picker__dropdown-item${isActive ? ' is-active' : ''}`}
                      onClick={() => {
                        setMetadataField(field.value);
                        setMetadataMenuOpen(false);
                      }}
                    >
                      {field.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {DATE_METADATA_FIELDS.has(metadataField) ? (
            <>
              <input
                type="date"
                value={metadataDateStart}
                onChange={(event) => setMetadataDateStart(event.target.value)}
              />
              <input
                type="date"
                value={metadataDateEnd}
                onChange={(event) => setMetadataDateEnd(event.target.value)}
              />
            </>
          ) : TIME_METADATA_FIELDS.has(metadataField) ? (
            <>
              <input
                type="time"
                value={metadataTimeStart}
                onChange={(event) => setMetadataTimeStart(event.target.value)}
              />
              <input
                type="time"
                value={metadataTimeEnd}
                onChange={(event) => setMetadataTimeEnd(event.target.value)}
              />
            </>
          ) : NUMERIC_METADATA_FIELDS.has(metadataField) ? (
            <>
              <input
                type="number"
                inputMode="decimal"
                placeholder="Min"
                value={metadataRangeMin}
                onChange={(event) => setMetadataRangeMin(event.target.value)}
              />
              <input
                type="number"
                inputMode="decimal"
                placeholder="Max"
                value={metadataRangeMax}
                onChange={(event) => setMetadataRangeMax(event.target.value)}
              />
              <p className="photo-picker__input-hint">
                {metadataField === 'exposureTime'
                  ? 'Examples: 1/250 s, 0.004 s'
                  : metadataField === 'fNumber'
                    ? 'Examples: f/2.8, f/8'
                    : 'Units depend on the field (e.g. seconds, decimal degrees, meters).'}
              </p>
            </>
          ) : (
            <input
              type="text"
              placeholder="Value (partial match)"
              value={metadataValue}
              onChange={(event) => setMetadataValue(event.target.value)}
            />
          )}
          <button type="button" className="photo-picker__chip" onClick={handleApplyMetadataFilter}>
            Apply metadata
          </button>
        </div>
      </div>

      {showMetaFilterNote && (
        <p className="photo-picker__advanced-note">
          Metadata-based filters and sorting require the metadata scan to finish and can take several minutes.
          Recommended only for folders with a limited number of images.
        </p>
      )}

      <div className="photo-picker__section">
        <div className="photo-picker__section-title">Quick selection</div>
        <div className="photo-picker__chip-row">
          <button type="button" className="photo-picker__chip" onClick={handleSelectToday}>
            Only today
          </button>
          <button type="button" className="photo-picker__chip" onClick={handleSelectLast48h}>
            Last 48h
          </button>
          <button type="button" className="photo-picker__chip" onClick={handleSelectLast7Days}>
            Last 7 days
          </button>
          <button type="button" className="photo-picker__chip" onClick={handleSelectLast30Days}>
            Last 30 days
          </button>
          <button type="button" className="photo-picker__chip" onClick={handleSelectRecentSession}>
            Recent session
          </button>
        </div>
      </div>

      <div className="photo-picker__section">
        <div className="photo-picker__section-title">Metadata helpers</div>
        <div className="photo-picker__chip-row">
          <button type="button" className="photo-picker__chip" onClick={handleSelectNoGps}>
            Only without GPS
          </button>
          <button type="button" className="photo-picker__chip" onClick={handleSelectNoExif}>
            Only without EXIF
          </button>
          <button type="button" className="photo-picker__chip" onClick={handleSelectLandscape}>
            Landscape
          </button>
          <button type="button" className="photo-picker__chip" onClick={handleSelectPortrait}>
            Portrait
          </button>
        </div>
      </div>

      <div className="photo-picker__section">
        <div className="photo-picker__section-title">Exclude helpers</div>
        <div className="photo-picker__chip-row">
          <button type="button" className="photo-picker__chip" onClick={handleExcludeScreenshots}>
            Exclude screenshots
          </button>
          <button type="button" className="photo-picker__chip" onClick={handleExcludeCopies}>
            Exclude copies
          </button>
        </div>
      </div>

      <div className="photo-picker__section">
        <div className="photo-picker__section-title">Date</div>
        <div className="photo-picker__time-row">
          <input type="date" value={dateStart} onChange={(event) => setDateStart(event.target.value)} />
          <span>to</span>
          <input type="date" value={dateEnd} onChange={(event) => setDateEnd(event.target.value)} />
          <button type="button" className="photo-picker__chip" onClick={handleApplyDateRange}>
            Apply
          </button>
        </div>
      </div>

      <div className="photo-picker__section">
        <div className="photo-picker__section-title">Time window</div>
        <div className="photo-picker__time-row">
          <input type="time" value={timeStart} onChange={(event) => setTimeStart(event.target.value)} />
          <span>to</span>
          <input type="time" value={timeEnd} onChange={(event) => setTimeEnd(event.target.value)} />
          <button type="button" className="photo-picker__chip" onClick={handleApplyTimeWindow}>
            Apply
          </button>
        </div>
      </div>

      <div className="photo-picker__section">
        <div className="photo-picker__section-title">Name filters</div>
        <div className="photo-picker__name-row">
          <input
            type="text"
            placeholder="Contains"
            value={nameInclude}
            onChange={(event) => setNameInclude(event.target.value)}
          />
          <input
            type="text"
            placeholder="Exclude contains"
            value={nameExclude}
            onChange={(event) => setNameExclude(event.target.value)}
          />
          <button type="button" className="photo-picker__chip" onClick={handleApplyNameFilters}>
            Apply
          </button>
        </div>
        <div className="photo-picker__name-row">
          <input
            type="text"
            placeholder="Regex (power users)"
            value={regexFilter}
            onChange={(event) => setRegexFilter(event.target.value)}
          />
          <button type="button" className="photo-picker__chip" onClick={handleApplyRegexFilter}>
            Apply regex
          </button>
        </div>
        <div className="photo-picker__name-row">
          <input
            type="text"
            placeholder="Prefix (e.g. IMG_)"
            value={rangePrefix}
            onChange={(event) => setRangePrefix(event.target.value)}
          />
          <input
            type="text"
            placeholder="From"
            value={rangeStart}
            onChange={(event) => setRangeStart(event.target.value)}
          />
          <input
            type="text"
            placeholder="To"
            value={rangeEnd}
            onChange={(event) => setRangeEnd(event.target.value)}
          />
          <button type="button" className="photo-picker__chip" onClick={handleApplyRangeFilter}>
            Apply range
          </button>
        </div>
      </div>

      {ignoredCount > 0 && (
        <div className="photo-picker__section">
          <div className="photo-picker__section-title">Ignored files</div>
          <p className="photo-picker__ignored-meta">
            {ignoredCount} files skipped (unsupported extension).
          </p>
          {ignoredCount > ignoredFiles.length && (
            <p className="photo-picker__ignored-meta">
              Showing first {ignoredFiles.length} items.
            </p>
          )}
          {ignoredFiles.length > 0 && (
            <div className="photo-picker__ignored-list">
              {ignoredFiles.map((file) => (
                <div key={file.relativePath} className="photo-picker__ignored-item">
                  {file.relativePath}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} variant="wide" closeOnOverlayClick={false}>
      <div
        ref={pickerRef}
        className={`photo-picker${useSidePanel ? ' photo-picker--advanced-side' : ''}`}
      >
        <div className="photo-picker__header">
          <div>
            <p className="photo-picker__eyebrow">Direct Write</p>
            {!isMobile && <h3>Select files to analyze</h3>}
            <div className="photo-picker__meta">
              {rootName && <span className="photo-picker__meta-root">{rootName}</span>}
              <div className="photo-picker__meta-item">
                <span className="photo-picker__meta-count" translate="no">
                  {entries.length}
                </span>
                <span>files</span>
              </div>
              <div className="photo-picker__meta-item">
                <span className="photo-picker__meta-count" translate="no">
                  {selectedCount}
                </span>
                <span>selected</span>
              </div>
              {multiSelectMode && (
                <button
                  type="button"
                  className="photo-picker__mode-badge"
                  onClick={() => {
                    setMultiSelectMode(false);
                    setRangeAnchorId(null);
                  }}
                >
                  MULTI-SELECT ACTIVE
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={`photo-picker__body ${showAdvancedDrawer ? 'photo-picker__body--locked' : ''}`}>
          <div className="photo-picker__toolbar">
            {!isMobile && (
              <div className="photo-picker__search">
                <input
                  type="search"
                  placeholder="Search by name or path"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
            )}
            <div className="photo-picker__actions">
              <button type="button" className="photo-picker__action-btn" onClick={handleSelectAll}>
                Select all
              </button>
              <button
                type="button"
                className="photo-picker__action-btn photo-picker__action-btn--ghost"
                onClick={handleClearSelection}
              >
                Clear
              </button>
              <button
                type="button"
                className="photo-picker__action-btn photo-picker__action-btn--ghost"
                disabled={selectedCount === 0}
                onClick={() => setShowSelectedOnly((prev) => !prev)}
              >
                {showSelectedOnly ? 'Show all' : 'Show selected only'}
              </button>
              <button
                type="button"
                className="photo-picker__action-btn photo-picker__action-btn--ghost"
                onClick={() => setShowAdvanced((prev) => !prev)}
              >
                {showAdvanced ? 'Hide options' : 'More options'}
              </button>
            </div>
          </div>

          {showAdvanced && !isMobile && useSidePanel && (
            <div className="photo-picker__advanced">
              {advancedSections}
            </div>
          )}
          {showAdvancedDrawer && (
            <div className="photo-picker__advanced-drawer" role="dialog" aria-label="More options">
              <div className="photo-picker__advanced-drawer-header">
                <div className="photo-picker__advanced-drawer-title">More options</div>
                <button
                  type="button"
                  className="photo-picker__advanced-drawer-close"
                  aria-label="Close more options"
                  onClick={() => setShowAdvanced(false)}
                >
                  x
                </button>
              </div>
              <div className="photo-picker__advanced-drawer-content">
                {advancedSections}
              </div>
            </div>
          )}

          {warnings.length > 0 && (
            <div className="photo-picker__warning">{warnings.join(' ')}</div>
          )}

          <div
            ref={gridRef}
            className="photo-picker__grid"
            onScroll={handleGridScroll}
          >
            <div className="photo-picker__grid-spacer" style={{ height: totalHeight }}>
              {visibleRows.map((row) => {
                const start = row * columns;
                const rowItems = sortedEntries.slice(start, start + columns);
                return (
                  <div
                    key={row}
                    className="photo-picker__row"
                    style={{
                      transform: `translateY(${row * rowHeight}px)`,
                      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                    }}
                  >
                    {rowItems.map((entry) => {
                      const isItemSelected = isSelectedId(entry.id);
                      const thumbValue = thumbMapRef.current.get(entry.id);
                      return (
                        <button
                          key={entry.id}
                          type="button"
                          className={`photo-picker__tile ${isItemSelected ? 'is-selected' : ''}`}
                          aria-pressed={isItemSelected}
                          onPointerDown={(event) => handlePointerDown(entry.id, event)}
                          onPointerUp={(event) => handlePointerUp(entry.id, event)}
                          onPointerLeave={handlePointerLeave}
                          onContextMenu={(event) => event.preventDefault()}
                        >
                          <div className="photo-picker__thumb">
                            {thumbValue ? (
                              typeof thumbValue === 'string' ? (
                                thumbValue.startsWith('ERROR:') ? (
                                <div className="photo-picker__thumb-placeholder" style={{ backgroundColor: '#ffdede', color: '#d32f2f', fontSize: '8px', padding: '2px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                  {thumbValue.replace('ERROR:', '')}
                                </div>
                                ) : thumbValue.startsWith('STATUS:') ? (
                                <div className="photo-picker__thumb-placeholder" style={{ backgroundColor: '#fff9c4', color: '#f57f17', fontSize: '8px', padding: '2px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                  {thumbValue.replace('STATUS:', '')}
                                </div>
                                ) : (
                                  <img src={thumbValue} alt={entry.name} loading="lazy" decoding="async" />
                                )
                              ) : (
                                <ThumbCanvas
                                  bitmap={thumbValue}
                                  size={thumbProfile.thumbSize}
                                  alt={entry.name}
                                  highQuality={highQualityThumbs}
                                />
                              )
                            ) : (
                              <div className="photo-picker__thumb-placeholder" />
                            )}
                          </div>
                          <div className="photo-picker__flag" />
                          <div className="photo-picker__label" title={entry.relativePath}>
                            {entry.name}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {!scanning && sortedEntries.length === 0 && (
              <div className="photo-picker__empty">
                No files match the current filters.
              </div>
            )}
            {scanning && entries.length === 0 && (
              <div className="photo-picker__empty">Scanning folder...</div>
            )}
          </div>
        </div>

        <div className="photo-picker__footer">
          <div className="photo-picker__footer-meta">
            {ruleStatus && <div>{ruleStatus}</div>}
            {!ruleStatus && preScanStatus && (
              <div>
                {preScanStatus}
                {preScanProgress !== null ? ` ${preScanProgress}%` : ''}
              </div>
            )}
            {!ruleStatus && !preScanStatus && scanning && (
              <div>Scanning in progress. You can start selecting now.</div>
            )}
            {thumbStatus && <div className="photo-picker__thumb-status">{thumbStatus}</div>}
            {debugMode ? (
              <div style={{ fontSize: '9px', opacity: 0.6, marginTop: '2px' }}>
                <div>{thumbDebugInfo ?? 'debug=1'}</div>
                {thumbTimingInfo && <div>{thumbTimingInfo}</div>}
                <div style={{ fontFamily: 'monospace' }}>
                  Q:{thumbQueueRef.current.length} A:{thumbActiveRef.current} V:{visibleIdsRef.current.size} L:{loadingThumbsRef.current.size} M:{thumbMapRef.current.size}
                </div>
              </div>
            ) : null}
          </div>
          <div className="photo-picker__footer-actions">
            <button type="button" className="modal-btn modal-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="modal-btn modal-btn--primary"
              onClick={handleConfirm}
              disabled={selectedCount === 0 || scanning}
            >
              Use selection
            </button>
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default DirectWritePickerModal;
